<template>
  <div class="stl-root" :class="{ 'stl-filters-collapsed': !filtersExpanded }">

    <b-modal title="Here are details on the statuses and filters used for the VSM" size="lg" v-model="view_more_info" ok-only>
      <br/>
      Use this page to view and manage support tickets.
      <br/><br/>
      <b>Create A Support Ticket</b>
      <br/><br/>
      Click 'Create Ticket' to get started with a new ticket
      <br/><br/>
      <b>Search For A Support Ticket</b>
      <br/><br/>
      Use the search tools to find existing tickets
      <br/><br/>
      <b>Update A Support Ticket</b>
      <br/><br/>
      Click the Edit Icon to update a ticket
    </b-modal>

    <!-- filter bar: the Ticket Board's controls (support_ticket_board.vue)
         as chips with counts. every ticket the api will show this viewer is
         loaded once and every dimension narrows client-side (client_filter),
         so a chip's count is the rows it would leave given every other
         filter. staff get the scope row's customer / assignee / created-by
         controls and the urgency and flag chips; everyone gets status, type,
         feature and search. -->
    <div class="stl-filter-row-top stl-fbar-top">
      <!-- phone-only filters toggle, mirroring the boards: every chip row
           folds away and a phone opens on the search box alone. the badge
           counts what is hidden. display:none above 768px. -->
      <button
        type="button"
        class="stl-filter-toggle stl-mobile-only"
        :aria-expanded="filtersExpanded ? 'true' : 'false'"
        @click="filtersExpanded = !filtersExpanded"
      >
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="stl-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>
      <!-- phone only: the search stays reachable while the chip rows are
           folded away. on a desktop the same field, with reload, sits at
           the right end of the chip row below. -->
      <span class="stl-search-slot stl-mobile-only">
        <b-form-input class="stl-fbar-input" v-model="search_text" placeholder="search tickets"/>
      </span>
      <!-- under horizon these live in the help-center header rail; classic
           renders no rail, so they sit here. the create button is the
           page's primary action and never folds away. -->
      <span v-if="!horizonNavActive" style="white-space: nowrap">
        &nbsp;<b-button variant="success" size="sm" class="stl-create-btn" title="Create a new support ticket" aria-label="Create ticket" @click="openCreate()"><i class="fa fa-plus stl-create-icon-only" aria-hidden="true" style="display:none;"></i><span class="stl-create-label-full" style="color:white">Create Ticket</span></b-button>
      </span>
      <span v-if="!horizonNavActive && effectiveRole === 'admin'" style="white-space: nowrap">
        &nbsp;<support-view-toggle active="list" />
      </span>
    </div>

    <!-- scope row: whose tickets -->
    <div class="stl-scope-row stl-fbar-collapsible">
      <template v-if="effectiveRole === 'admin'">
        <button type="button" :class="['stl-chip', { 'stl-chip--active': assignee_filter === MINE }]"
                v-show="assignee_counts[MINE] || assignee_filter === MINE"
                :aria-pressed="assignee_filter === MINE ? 'true' : 'false'"
                v-on:click.prevent="assignee_filter = MINE">Mine <span class="stl-chip-count">{{ assignee_counts[MINE] || 0 }}</span></button>
        <button type="button" :class="['stl-chip', { 'stl-chip--active': assignee_filter === MY_CREATED }]"
                v-show="assignee_counts[MY_CREATED] || assignee_filter === MY_CREATED"
                :aria-pressed="assignee_filter === MY_CREATED ? 'true' : 'false'"
                v-on:click.prevent="assignee_filter = MY_CREATED">My Created <span class="stl-chip-count">{{ assignee_counts[MY_CREATED] || 0 }}</span></button>
        <button type="button" :class="['stl-chip', { 'stl-chip--active': assignee_filter === '' }]"
                :aria-pressed="assignee_filter === '' ? 'true' : 'false'"
                v-on:click.prevent="assignee_filter = ''">Everyone <span class="stl-chip-count">{{ assignee_counts[''] || 0 }}</span></button>
        <span class="stl-btn-group stl-dev-team-chips" role="group" aria-label="Filter by person">
          <button type="button" class="stl-dev-chip"
                  v-show="assignee_counts[UNASSIGNED] || assignee_filter === UNASSIGNED"
                  :class="{ 'stl-dev-chip--selected': assignee_filter === UNASSIGNED }"
                  :aria-pressed="assignee_filter === UNASSIGNED ? 'true' : 'false'"
                  :title="`Unassigned — ${assignee_counts[UNASSIGNED] || 0}`"
                  :aria-label="`Unassigned — ${assignee_counts[UNASSIGNED] || 0}`"
                  v-on:click.prevent="toggle_assignee(UNASSIGNED)">
            <span class="stl-dev-chip-icon stl-dev-chip-icon--unassigned" aria-hidden="true"><i class="fa fa-user"></i></span>
            <span class="stl-dev-chip-count" aria-hidden="true">{{ assignee_counts[UNASSIGNED] || 0 }}</span>
          </button>
          <button v-for="member in team_chips" :key="member.user" type="button" class="stl-dev-chip"
                  v-show="assignee_counts[member.username] || is_team_chip_selected(member)"
                  :class="{ 'stl-dev-chip--selected': is_team_chip_selected(member) }"
                  :aria-pressed="is_team_chip_selected(member) ? 'true' : 'false'"
                  :title="`${member.name} — ${assignee_counts[member.username] || 0}`"
                  :aria-label="`${member.name} — ${assignee_counts[member.username] || 0}`"
                  v-on:click.prevent="toggle_assignee(member.username)">
            <img :src="member.image" :alt="member.name" class="stl-dev-chip-img" />
            <span class="stl-dev-chip-count" aria-hidden="true">{{ assignee_counts[member.username] || 0 }}</span>
          </button>
        </span>
      </template>
      <!-- status is a real filter here (the list is the archive surface):
           one-of, the same groups the board's presets use plus the closed
           ones. hidden at zero unless it is the one pressed. -->
      <span class="stl-group" role="group" aria-label="Status">
        <span class="stl-group-label">Status</span>
        <!-- the first three (plus the pressed one) show; the rest fold
             behind a +N chip, like the Feature row -->
        <button v-for="opt in shown_status_filters" :key="'s-' + opt.value" type="button"
                :class="['stl-chip', { 'stl-chip--active': status_preset === opt.value }]"
                :aria-pressed="status_preset === opt.value ? 'true' : 'false'"
                v-on:click.prevent="status_preset = opt.value">
          {{ opt.text }}<span class="stl-chip-count">{{ status_counts[opt.value] || 0 }}</span>
        </button>
        <button v-if="hidden_status_count || status_expanded" type="button" class="stl-chip stl-chip--more"
                :aria-expanded="status_expanded ? 'true' : 'false'"
                :title="status_expanded ? 'Show fewer statuses' : 'Show every status'"
                v-on:click.prevent="status_expanded = !status_expanded">
          <template v-if="status_expanded"><i class="fa fa-angle-left" aria-hidden="true"></i>less</template>
          <template v-else>+{{ hidden_status_count }} more</template>
        </button>
      </span>
      <span v-if="effectiveRole === 'admin'" class="stl-group" role="group" aria-label="Urgency">
        <span class="stl-group-label">Urgency</span>
        <button v-for="opt in URGENCY_FILTERS" :key="'u-' + opt.value" type="button"
                v-show="urgency_counts[opt.value] || urgency_filters.includes(opt.value)"
                :class="['stl-chip', 'stl-chip--tone', opt.variant, { 'stl-chip--active': urgency_filters.includes(opt.value) }]"
                :aria-pressed="urgency_filters.includes(opt.value) ? 'true' : 'false'"
                v-on:click.prevent="toggle_filter('urgency_filters', opt.value)">
          <i class="fa" :class="opt.icon" aria-hidden="true"></i>{{ opt.text }}
          <span class="stl-chip-count">{{ urgency_counts[opt.value] || 0 }}</span>
        </button>
      </span>
      <span v-if="effectiveRole === 'admin'" class="stl-group" role="group" aria-label="Flags">
        <button type="button" :class="['stl-chip', 'stl-chip--tone', 'stl-flag-vip', { 'stl-chip--active': vip_only }]"
                :aria-pressed="vip_only ? 'true' : 'false'" title="Only tickets for VIP accounts"
                v-on:click.prevent="vip_only = !vip_only">
          <i class="fa fa-star" aria-hidden="true"></i>VIP<span class="stl-chip-count">{{ flag_counts.vip || 0 }}</span>
        </button>
        <button type="button" :class="['stl-chip', 'stl-chip--tone', 'stl-flag-billable', { 'stl-chip--active': billable_only }]"
                :aria-pressed="billable_only ? 'true' : 'false'" title="Only tickets marked billable"
                v-on:click.prevent="billable_only = !billable_only">
          <i class="fa fa-usd" aria-hidden="true"></i>Billable<span class="stl-chip-count">{{ flag_counts.billable || 0 }}</span>
        </button>
      </span>

      <!-- search, anchored to the row's far right (the board's arrangement) -->
      <span class="stl-search-right stl-desktop-only">
        <b-form-input class="stl-fbar-input stl-fbar-input--right" v-model="search_text" placeholder="search tickets" size="sm"/>
      </span>
    </div>

    <!-- second row: what the tickets are -->
    <div class="stl-filter-row stl-fbar-collapsible">
      <span class="stl-group" role="group" aria-label="Type">
        <span class="stl-group-label">Type</span>
        <button v-for="opt in TYPE_FILTERS" :key="'t-' + opt.value" type="button"
                v-show="type_counts[opt.value] || type_filters.includes(opt.value)"
                :class="['stl-chip', 'stl-chip--tone', opt.variant, { 'stl-chip--active': type_filters.includes(opt.value) }]"
                :aria-pressed="type_filters.includes(opt.value) ? 'true' : 'false'"
                v-on:click.prevent="toggle_filter('type_filters', opt.value)">
          <i class="fa" :class="opt.icon" aria-hidden="true"></i>{{ opt.text }}
          <span class="stl-chip-count">{{ type_counts[opt.value] || 0 }}</span>
        </button>
      </span>
      <span class="stl-group" role="group" aria-label="Feature">
        <span class="stl-group-label">Feature</span>
        <button v-for="opt in shown_feature_filters" :key="'f-' + opt.value" type="button"
                :class="['stl-chip', 'stl-chip--tone', 'stl-feat-' + opt.value, { 'stl-chip--active': category_filters.includes(opt.value) }]"
                :aria-pressed="category_filters.includes(opt.value) ? 'true' : 'false'"
                v-on:click.prevent="toggle_filter('category_filters', opt.value)">
          {{ opt.text }}<span class="stl-chip-count">{{ feature_counts[opt.value] || 0 }}</span>
        </button>
        <button v-if="hidden_feature_count || features_expanded" type="button" class="stl-chip stl-chip--more"
                :aria-expanded="features_expanded ? 'true' : 'false'"
                :title="features_expanded ? 'Show fewer features' : 'Show every feature'"
                v-on:click.prevent="features_expanded = !features_expanded">
          <template v-if="features_expanded"><i class="fa fa-angle-left" aria-hidden="true"></i>less</template>
          <template v-else>+{{ hidden_feature_count }} more</template>
        </button>
      </span>
      <!-- the two pickers (staff) and reload, anchored to the row's far right -->
      <span class="stl-search-right stl-desktop-only">
        <template v-if="effectiveRole === 'admin'">
          <b-form-select v-model="selected_tenant" :options="search_tenant_options" size="sm"
                         class="stl-tenant-select" aria-label="Customer" />
          <b-form-select v-model="selected_created_by" :options="created_by_options" size="sm"
                         class="stl-scope-select" aria-label="Filter by creator" />
        </template>
        <b-button size="sm" variant="outline-secondary" :disabled="loading" title="Reload tickets" aria-label="Reload"
                  v-on:click.prevent="load_data"><i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i></b-button>
      </span>
    </div>

    <!-- what is narrowing the list right now, one removable pill each -->
    <div v-if="active_filter_pills.length" class="stl-active stl-fbar-collapsible">
      <span class="stl-active-label"><i class="fa fa-filter" aria-hidden="true"></i> Filtering by</span>
      <button v-for="pill in active_filter_pills" :key="pill.key" type="button" class="stl-active-pill"
              :title="'Remove ' + pill.dim + ' filter'" v-on:click.prevent="remove_filter_pill(pill)">
        <span class="stl-active-dim">{{ pill.dim }}</span> {{ pill.text }}
        <i class="fa fa-times" aria-hidden="true"></i>
      </button>
      <button type="button" class="stl-link-btn stl-active-clear" v-on:click.prevent="clear_filters">Clear all</button>
    </div>

    <!-- mobile list-view: list-to-detail navigation matching the help
         articles pattern (≤768px cut-off). the card acts as the primary
         tap target → /support_ticket_details?tenant_id=…&id=…. on
         tablet/desktop (>768px) the full b-table below takes over. -->
    <div v-if="isMobileView" class="stl-mobile-list">
      <!-- skeleton cards while load_data runs. shape mirrors the real
           card (id chip + priority tag pill, multi-line subject, meta
           row, assignee) so the page settles into final content
           with minimal layout shift. -->
      <template v-if="loading">
        <div v-for="n in 4" :key="`stl-sk-${n}`" class="stl-mobile-card stl-mobile-card--skeleton">
          <div class="stl-mobile-card-head">
            <span class="stl-skeleton stl-skeleton--id"></span>
            <span class="stl-skeleton stl-skeleton--tag"></span>
          </div>
          <span class="stl-skeleton stl-skeleton--subject"></span>
          <span v-if="n % 2 === 0" class="stl-skeleton stl-skeleton--subject stl-skeleton--subject-2"></span>
          <div class="stl-skeleton-meta-row">
            <span class="stl-skeleton stl-skeleton--meta-status"></span>
            <span class="stl-skeleton stl-skeleton--meta-tenant"></span>
            <span class="stl-skeleton stl-skeleton--meta-time"></span>
          </div>
          <span class="stl-skeleton stl-skeleton--admin"></span>
        </div>
      </template>
      <div
        v-for="item in paginatedItems"
        v-if="!loading"
        :key="`mbl-${item.tenant_id}-${item.id}`"
        role="button" tabindex="0"
        @click="openTicketModal(item)"
        @keydown.enter.space.prevent="openTicketModal(item)"
        class="stl-mobile-card"
        :class="{
          'stl-mobile-card--muted': ['completed','cancelled'].includes(item.ticket_status),
          'stl-mobile-card--vip': !!getTenantVip(item.tenant_id),
        }">
        <div class="stl-mobile-card-head">
          <span class="stl-mobile-card-id">#{{ item.id }}</span>
          <span v-if="getPriorityTag(item.priority)"
                class="priority-tag priority-tag--labeled"
                :class="[
                  getPriorityTag(item.priority).variant,
                  { 'priority-tag-muted': ['completed','cancelled'].includes(item.ticket_status) },
                  { 'priority-tag--strike': item.priority === 'urgent' && !['completed','cancelled'].includes(item.ticket_status) },
                ]">
            <i class="fa" :class="getPriorityTag(item.priority).icon" aria-hidden="true"></i>
            <span class="priority-tag-label">{{ getPriorityTag(item.priority).compactLabel }}</span>
          </span>
        </div>
        <div class="stl-mobile-card-subject">{{ item.subject || '(no subject)' }}</div>
        <div class="stl-mobile-card-meta">
          <span class="stl-mobile-card-status">{{ statusShortLabel(item.ticket_status) }}</span>
          <span class="stl-mobile-card-sep">·</span>
          <span class="stl-mobile-card-tenant" :class="{ 'is-vip': !!getTenantVip(item.tenant_id) }">
            {{ getOptionText(user.tenant_options, item.tenant_id) }}
          </span>
          <span class="stl-mobile-card-sep">·</span>
          <span class="stl-mobile-card-updated">{{ formatDateTimeCompact(item.updated_at) }}</span>
          <!-- read-only for everyone here: the whole card opens the ticket modal, so
               a live checkbox would fight the navigation gesture. toggling
               stays on the desktop table and the details page. -->
          <span v-if="item.billable" class="stl-mobile-card-billable">
            <i class="fa fa-dollar" aria-hidden="true"></i> Billable
          </span>
        </div>
        <!-- assignee shows for every role here too, matching the desktop
             Assigned column. reads inlineAssignedOptions (the editor list the
             desktop cell uses) rather than assigned_options (the filter-bar
             list, which leads with the {value:'', text:'All'} sentinel and has
             no null -> Unassigned entry). Both happen to render the same text
             for the values that actually occur -- assigned_to is only ever a
             username or null -- so this is alignment, not a live bug fix. -->
        <div class="stl-mobile-card-admin">
          <span class="stl-mobile-card-assigned">{{ getOptionText(inlineAssignedOptions, item.assigned_to) || 'Unassigned' }}</span>
        </div>
      </div>
      <div v-if="!loading && items.length === 0" class="stl-mobile-empty">No tickets match your filters.</div>
    </div>

    <div v-show="!isMobileView" class="stl-table-wrap">
      <div class="stl-table-inner">
      <!-- a click anywhere on a row opens the ticket modal, except on the
           inline-edit cells and the action controls (onRowClicked sorts
           that out from the click target) -->
      <b-table :busy="loading"
               @row-clicked="onRowClicked"
               :items="sortedItems"
               :fields="filteredFields"
               :current-page="currentPage"
               :per-page="perPage"
               :sort-by.sync="sortBy"
               :sort-desc.sync="sortDesc"
               no-local-sorting
               :tbody-tr-class="rowClass"
               responsive>

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="id" slot-scope="row">
          <!-- a real href: a plain click opens the modal (.exact leaves
               modifier clicks alone), while ctrl/cmd/middle-click and
               "open in new tab" follow the deep link to the full page -->
          <a :href="ticketHref(row.item)" @click.exact.prevent="openTicketModal(row.item)" :title="'Open ticket #' + row.item.id">
            {{ row.item.id }}
          </a>
        </template>

        <template slot="tenant_id" slot-scope="row">
          <span
            class="tenant-name"
            :class="{
              'is-vip': !!getTenantVip(row.item.tenant_id),
              'is-muted': ['completed', 'cancelled'].includes(row.item.ticket_status)
            }"
            :title="getTenantVip(row.item.tenant_id) ? ('VIP — ' + getTenantVip(row.item.tenant_id).vip_reason) : ''"
          >{{ getOptionText(user.tenant_options, row.item.tenant_id) }}</span>
        </template>

        <template slot="created_at" slot-scope="row">
          <span class="stl-dt" :title="row.item.created_at">{{ formatDateTimeCompactYear(row.item.created_at) }}</span>
        </template>

        <template slot="updated_at" slot-scope="row">
          <span class="stl-dt" :class="{ 'stl-dt-overdue': row.item.updated_at_overdue }" :title="row.item.updated_at">
            {{ formatDateTimeCompactYear(row.item.updated_at) }}
          </span>
        </template>

        <template slot="estimated_finish_date" slot-scope="row">
          <span class="stl-dt" :title="row.item.estimated_finish_date || ''">
            {{ formatTargetDateCompact(row.item.estimated_finish_date) }}
          </span>
        </template>

        <!-- billable is a straight toggle rather than the click-to-edit
             pattern the other inline columns use: a checkbox is already a
             one-gesture control, so an extra "click to reveal the editor"
             step would only get in the way. saves through the same
             updateTicketInline path (send_email=false) so flipping it never
             emails the customer. -->
        <template slot="billable" slot-scope="row">
          <b-form-checkbox
            :checked="!!row.item.billable"
            :disabled="effectiveRole !== 'admin' || isInlineSaving(row.item, 'billable')"
            class="stl-billable-check"
            @change="updateTicketInline(row.item, 'billable', $event)"
          />
        </template>

        <template slot="assigned_to" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'assigned_to')"
              :value="row.item.assigned_to"
              :options="inlineAssignedOptions"
              :disabled="isInlineSaving(row.item, 'assigned_to')"
              size="sm"
              class="stl-inline-select"
              @change="updateTicketInline(row.item, 'assigned_to', $event)"
              @blur="onInlineEditBlur(row.item, 'assigned_to')"
            />
            <span
              v-else
              class="stl-inline-readonly stl-person-display"
              tabindex="0"
              role="button"
              :title="displayUserName(row.item.assigned_to) || 'Unassigned'"
              @click="startInlineEdit(row.item, 'assigned_to', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'assigned_to', $event)"
            >
              <img
                v-if="initekTeamImage(row.item.assigned_to)"
                :src="initekTeamImage(row.item.assigned_to)"
                :alt="displayUserName(row.item.assigned_to)"
                class="stl-team-avatar"
              />
              <span v-else>{{ getOptionText(inlineAssignedOptions, row.item.assigned_to) || 'Unassigned' }}</span>
            </span>
          </template>
          <div
            v-else
            class="stl-person-display"
            :title="initekTeamImage(row.item.assigned_to) ? displayUserName(row.item.assigned_to) : null"
          >
            <img
              v-if="initekTeamImage(row.item.assigned_to)"
              :src="initekTeamImage(row.item.assigned_to)"
              :alt="displayUserName(row.item.assigned_to)"
              class="stl-team-avatar"
            />
            <span v-else>{{ getOptionText(inlineAssignedOptions, row.item.assigned_to) || 'Unassigned' }}</span>
          </div>
        </template>

        <template slot="username" slot-scope="row">
          {{ row.item.created_by_name || row.item.username.split('@')[0] }}
        </template>

        <template slot="updated_by" slot-scope="row">
          <div
            v-if="row.item.updated_by"
            class="stl-person-display"
            :title="initekTeamImage(row.item.updated_by)
              ? displayUserName(row.item.updated_by, row.item.updated_by_name)
              : null"
          >
            <img
              v-if="initekTeamImage(row.item.updated_by)"
              :src="initekTeamImage(row.item.updated_by)"
              :alt="displayUserName(row.item.updated_by, row.item.updated_by_name)"
              class="stl-team-avatar"
            />
            <span v-else>{{ displayUserName(row.item.updated_by, row.item.updated_by_name) }}</span>
          </div>
        </template>

        <template slot="subject" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-input
              v-if="isInlineEditing(row.item, 'subject')"
              v-model="inlineDraft"
              size="sm"
              class="stl-inline-input stl-subject"
              :disabled="isInlineSaving(row.item, 'subject')"
              @keydown.enter.prevent="commitInlineSubject(row.item)"
              @keydown.esc.prevent="clearInlineEdit"
              @blur="onInlineSubjectBlur(row.item)"
            />
            <span
              v-else
              class="stl-subject stl-inline-readonly"
              tabindex="0"
              role="button"
              :title="row.item.subject || ''"
              @click="startInlineEdit(row.item, 'subject', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'subject', $event)"
            >{{ row.item.subject }}</span>
          </template>
          <span v-else class="stl-subject" :title="row.item.subject || ''">{{ row.item.subject }}</span>
        </template>
        <template slot="ticket_status" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'ticket_status')"
              :value="row.item.ticket_status"
              :options="inlineStatusOptions"
              :disabled="isInlineSaving(row.item, 'ticket_status')"
              size="sm"
              class="stl-inline-select stl-inline-select--status"
              @change="updateTicketInline(row.item, 'ticket_status', $event)"
              @blur="onInlineEditBlur(row.item, 'ticket_status')"
            />
            <span
              v-else
              class="stl-inline-readonly stl-status"
              tabindex="0"
              role="button"
              :title="getOptionText(status_options, row.item.ticket_status)"
              @click="startInlineEdit(row.item, 'ticket_status', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'ticket_status', $event)"
            >{{ statusShortLabel(row.item.ticket_status) }}</span>
          </template>
          <span v-else class="stl-status" :title="getOptionText(status_options, row.item.ticket_status)">
            {{ statusShortLabel(row.item.ticket_status) }}
          </span>
        </template>
        <template slot="HEAD_priority">
          Type
        </template>
        <template slot="priority" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'priority')"
              :value="row.item.priority"
              :options="inlinePriorityOptions"
              :disabled="isInlineSaving(row.item, 'priority')"
              size="sm"
              class="stl-inline-select stl-inline-select--type"
              @change="updateTicketInline(row.item, 'priority', $event)"
              @blur="onInlineEditBlur(row.item, 'priority')"
            />
            <span
              v-else-if="getPriorityTag(row.item.priority)"
              class="priority-tag priority-tag--labeled stl-inline-readonly"
              :class="[
                getPriorityTag(row.item.priority).variant,
                {
                  'priority-tag-muted': ['completed', 'cancelled'].includes(row.item.ticket_status),
                  'priority-tag--strike': row.item.priority === 'urgent' && !['completed', 'cancelled'].includes(row.item.ticket_status),
                }
              ]"
              tabindex="0"
              role="button"
              :title="getPriorityTag(row.item.priority).label"
              :aria-label="getPriorityTag(row.item.priority).label"
              @click="startInlineEdit(row.item, 'priority', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'priority', $event)"
            >
              <i class="fa" :class="getPriorityTag(row.item.priority).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getPriorityTag(row.item.priority).compactLabel }}</span>
            </span>
            <span
              v-else
              class="stl-inline-readonly"
              tabindex="0"
              role="button"
              :title="getOptionText(inlinePriorityOptions, row.item.priority)"
              @click="startInlineEdit(row.item, 'priority', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'priority', $event)"
            >{{ getOptionText(inlinePriorityOptions, row.item.priority) }}</span>
          </template>
          <template v-else>
            <span v-if="getPriorityTag(row.item.priority)"
                  class="priority-tag"
                  :class="[
                    getPriorityTag(row.item.priority).variant,
                    'priority-tag--labeled',
                    {
                      'priority-tag-muted': ['completed', 'cancelled'].includes(row.item.ticket_status),
                      'priority-tag--strike': row.item.priority === 'urgent' && !['completed', 'cancelled'].includes(row.item.ticket_status),
                    }
                  ]"
                  :title="getPriorityTag(row.item.priority).label"
                  :aria-label="getPriorityTag(row.item.priority).label">
              <i class="fa" :class="getPriorityTag(row.item.priority).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getPriorityTag(row.item.priority).compactLabel }}</span>
            </span>
            <span v-else>{{ getOptionText(priority_options, row.item.priority) }}</span>
          </template>
        </template>
        <template slot="category" slot-scope="row">
          {{getOptionText(category_options, row.item.category)}}
        </template>
        <template slot="urgency" slot-scope="row">
          <template v-if="effectiveRole === 'admin'">
            <b-form-select
              v-if="isInlineEditing(row.item, 'urgency')"
              :value="row.item.urgency || null"
              :options="inlineUrgencyOptions"
              :disabled="isInlineSaving(row.item, 'urgency')"
              size="sm"
              class="stl-inline-select"
              @change="updateTicketInline(row.item, 'urgency', $event)"
              @blur="onInlineEditBlur(row.item, 'urgency')"
            />
            <span
              v-else-if="getUrgencyTag(row.item.urgency)"
              class="priority-tag priority-tag--labeled stl-inline-readonly"
              :class="getUrgencyTag(row.item.urgency).variant"
              tabindex="0"
              role="button"
              :title="getUrgencyTag(row.item.urgency).label"
              :aria-label="getUrgencyTag(row.item.urgency).label"
              @click="startInlineEdit(row.item, 'urgency', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'urgency', $event)"
            >
              <i class="fa" :class="getUrgencyTag(row.item.urgency).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getUrgencyTag(row.item.urgency).compactLabel }}</span>
            </span>
            <span
              v-else
              class="stl-inline-readonly"
              tabindex="0"
              role="button"
              @click="startInlineEdit(row.item, 'urgency', $event)"
              @keydown.enter.space.prevent="startInlineEdit(row.item, 'urgency', $event)"
            >—</span>
          </template>
          <template v-else>
            <span
              v-if="getUrgencyTag(row.item.urgency)"
              class="priority-tag priority-tag--labeled"
              :class="getUrgencyTag(row.item.urgency).variant"
              :title="getUrgencyTag(row.item.urgency).label"
              :aria-label="getUrgencyTag(row.item.urgency).label"
            >
              <i class="fa" :class="getUrgencyTag(row.item.urgency).icon" aria-hidden="true"></i>
              <span class="priority-tag-label">{{ getUrgencyTag(row.item.urgency).compactLabel }}</span>
            </span>
            <span v-else>—</span>
          </template>
        </template>

        <template slot="actions" slot-scope="row">
          <!-- flex row keeps edit + delete on the same line; without it the
               <router-link> can collapse to block layout and push delete
               to the next row in narrow viewports. -->
          <div style="display: flex; align-items: center; gap: 4px;">
            <a
              :href="ticketHref(row.item)"
              class="stl-action-btn"
              title="Edit ticket"
              aria-label="Edit ticket"
              @click.exact.prevent="openTicketModal(row.item)"
            >
              <i class="fa fa-pencil" aria-hidden="true"></i>
            </a>
            <button
              v-if="user && user.role === 'admin'"
              type="button"
              class="stl-action-btn"
              :disabled="isPokeSaving(row.item)"
              title="Send update email to customer"
              aria-label="Send update email to customer"
              @click="pokeTicket(row.item)"
            >
              <i class="fa" :class="isPokeSaving(row.item) ? 'fa-spinner fa-spin' : 'fa-hand-o-up'" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="stl-action-btn stl-action-btn--danger"
              title="Delete ticket"
              aria-label="Delete ticket"
              @click="delete_support_ticket(row.item.tenant_id,row.item.id)"
            >
              <i class="fa fa-trash-o" aria-hidden="true"></i>
            </button>
          </div>
        </template>
      </b-table>
      </div>
    </div>

    <!-- pagination lives outside the desktop table wrapper so the mobile
         card list uses the same control (both views bind to
         currentPage + slice off items). -->
    <b-row>
      <b-col>
        <b-pagination
            v-model="currentPage"
            :total-rows="items.length"
            :per-page="perPage"
            align="center"
        >
        </b-pagination>
      </b-col>
    </b-row>


    <!-- the ticket opens in place (shared with the Ticket Board) rather
         than swapping the list for the full details page. deep links
         (Slack, email, ?ticket_id=) still land on the full page via
         help_center. on close the one ticket is refetched and its row
         patched, or dropped if it no longer matches the status filter. -->
    <support-ticket-modal ref="ticketModal" modal-id="stl-ticket-modal"
                          @hidden="onTicketModalHidden"
                          @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />
    <!-- "Add Ticket" opens the create form here too (the help-center rail
         button reaches it through openCreate). a new ticket reloads the
         list; a ticket whose attachments failed opens for the retry. -->
    <support-ticket-create-modal ref="createModal" modal-id="stl-ticket-create-modal"
                                 @created="onTicketCreated"
                                 @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />
  </div>
</template>

<script>
  import { delete_support_ticket, get_support_ticket, poke_support_ticket, search_support_tickets, update_support_ticket } from '../networking/apis'
  import { mapState } from "vuex";
  import { formatDateTimeCompact, formatDateTimeCompactYear } from "@/helpers/dates";
  import { INITEK_TEAM_FILTER_CHIPS, INITEK_TEAM_MEMBERS, initekTeamImageForUsername } from "@/helpers/initek_team_images";
  import { ticketInvolves, ticketPeople } from "@/helpers/support_tickets";
  import moment from "moment/moment";
  import horizonNav from '@/nav/horizon_nav_mixin'
  import SupportTicketModal from './support_ticket_modal.vue'
  import SupportTicketCreateModal from './support_ticket_create_modal.vue'
  import SupportViewToggle from './support_view_toggle.vue'

  const URGENCY_RANK = { low: 1, medium: 2, high: 3, emergency: 4 }
  const PRIORITY_RANK = { urgent: 5, minor: 4, question: 3, enhancement: 2, feature: 1 }

  // ---- the filter bar's vocabulary (mirrors support_ticket_board.vue) ----
  // the list's status groups, as one-of chips: the board's presets plus
  // the closed states, since the list is the archive surface
  const STATUS_GROUPS = {
    open: ['new', 'blocked', 'queued', 'review', 'progress', 'waiting', 'testing'],
    active: ['new', 'review', 'queued', 'progress'],
    waiting: ['testing', 'waiting'],
  }
  const STATUS_FILTERS = [
    { value: '', text: 'All' },
    { value: 'open', text: 'Open' },
    { value: 'active', text: 'Active' },
    { value: 'waiting', text: 'Waiting' },
    { value: 'testing', text: 'Testing' },
    { value: 'blocked', text: 'Blocked' },
    { value: 'completed', text: 'Completed' },
    { value: 'cancelled', text: 'Cancelled' },
  ]
  // the tag families, so a chip reads as the row's tag does
  const PRIORITY_TAG_META = {
    urgent:      { text: 'Major Bug',   icon: 'fa-bolt',     variant: 'stl-tone-danger' },
    minor:       { text: 'Minor Bug',   icon: 'fa-bug',      variant: 'stl-tone-orange' },
    question:    { text: 'Question',    icon: 'fa-question', variant: 'stl-tone-warning' },
    enhancement: { text: 'Enhancement', icon: 'fa-wrench',   variant: 'stl-tone-info' },
    feature:     { text: 'Feature',     icon: 'fa-star',     variant: 'stl-tone-success' },
  }
  const TYPE_FILTERS = ['urgent', 'minor', 'question', 'enhancement', 'feature'].map(v => ({ value: v, ...PRIORITY_TAG_META[v] }))
  const URGENCY_FILTERS = [
    { value: 'emergency', text: 'Emergency', icon: 'fa-bolt',                 variant: 'stl-tone-danger' },
    { value: 'high',      text: 'High',      icon: 'fa-exclamation-triangle', variant: 'stl-tone-orange' },
    { value: 'medium',    text: 'Medium',    icon: 'fa-exclamation',          variant: 'stl-tone-warning' },
    { value: 'low',       text: 'Low',       icon: 'fa-minus',                variant: 'stl-tone-info' },
  ]
  // the Feature chips come from category_options (data) minus its "All"
  // sentinel; see feature_filters()
  const FEATURE_CHIPS_SHOWN = 3
  const MINE = '__mine__'
  const MY_CREATED = '__my_created__'
  const UNASSIGNED = '__unassigned__'
  const CREATED_BY_SEPARATOR = '__sep__'

  export default {
    name: 'support_ticket_list',
    components: { SupportTicketModal, SupportTicketCreateModal, SupportViewToggle },
    mixins: [horizonNav],
    data() {
      return {
        toggle: {},
        view_more_info: false,
        auto_reload: true,
        file: null,
        msg: 'support_ticket_list',
        items: [],
        items_full: [],
        // the filter bar's state. everything narrows client-side over
        // items_full (client_filter); load_data only ever asks the api for
        // the whole set this viewer may see.
        selected_tenant: '',
        // '' = everyone, MINE = the signed-in user, MY_CREATED = tickets whose
        // Created By (requester) is the signed-in user, UNASSIGNED = nobody,
        // any other string = that assignee's username
        assignee_filter: '',
        MINE,
        MY_CREATED,
        UNASSIGNED,
        scope_initialized: false,
        // '' = anyone; else the exact stored username of the creator
        selected_created_by: '',
        // '' = every status; a group name or one status
        status_preset: 'open',
        STATUS_FILTERS,
        type_filters: [],
        urgency_filters: [],
        category_filters: [],
        // the Feature row shows three chips until this opens it
        features_expanded: false,
        // and the Status row, the same way
        status_expanded: false,
        TYPE_FILTERS,
        URGENCY_FILTERS,
        search_text: '',
        vip_only: false,
        billable_only: false,
        summary: [],
        loading: true,
        totalRows: 1,
        currentPage: 1,
        perPage: 100,
        // default to newest-first by ticket id; b-table syncs these back on
        // header clicks so the user can re-sort without losing the caret.
        sortBy: 'id',
        sortDesc: true,
        // mobile list→detail pattern (matches help articles ≤768px cut-off).
        // isMobileView drives the card view; resize handler keeps it in sync.
        isMobileView: false,
        // ≤768px folds every filter behind a toggle so a phone opens on the
        // search box alone. purely a CSS gate, so desktop never reads it.
        filtersExpanded: false,
        onWindowResize: null,
        pages: [],
        assigned_options: [
          {value: '', text: 'All'},
          {value: 'unassigned', text: 'Unassigned'},
          { value: 'ben@initekconsulting.com', text: 'Ben ODay' },
          // jen is a legacy short-username account — see support_ticket_details.vue assigned_options.
          { value: 'jen', text: 'Jen' },
          { value: 'chaz@initekconsulting.com', text: 'Chaz Watson' },
          { value: 'kai@initekconsulting.com', text: 'Kai Klein' },
          { value: 'evan@initekconsulting.com', text: 'Evan Anderson' },
        ],
        dev_team_filters: INITEK_TEAM_FILTER_CHIPS,
        category_options: [
          {value: '', text: 'All'},
          {value: 'general', text: 'General'},
          {value: 'billing', text: 'Billing'},
          {value: 'actuals', text: 'Actuals'},
          {value: 'alerts', text: 'Alerts'},
          {value: 'maps', text: 'Map Views'},
          {value: 'reports', text: 'View & Reports'},
          {value: 'vsm', text: 'VSM'},
          {value: 'data', text: 'Data Services'},
          {value: 'fleet-assistant', text: 'Fleet Assistant'},
          {value: 'driver-scorecard', text: 'Driver Manager'},
          {value: 'coaching-portal', text: 'Coaching Portal'},
          {value: 'erm', text: 'ERM'},
          {value: 'bus-sos', text: 'Bus SOS'},
        ],
        priority_options: [
          {value: '', text: 'All'},
          {value: 'question', text: 'Question/Request'},
          {value: 'minor', text: 'Minor Bug'},
          {value: 'urgent', text: 'Major Bug / System Issue'},
          {value: 'enhancement', text: 'Enhancement Request'},
          {value: 'feature', text: 'New Feature Request'},
        ],
        urgency_options: [
          {value: '', text: 'All'},
          {value: 'low', text: 'Low'},
          {value: 'medium', text: 'Medium'},
          {value: 'high', text: 'High'},
          {value: 'emergency', text: 'Emergency'},
        ],
        inlineAssignedOptions: [
          { value: null, text: 'Unassigned' },
          { value: 'ben@initekconsulting.com', text: 'Ben ODay' },
          { value: 'jen', text: 'Jen' },
          { value: 'chaz@initekconsulting.com', text: 'Chaz Watson' },
          { value: 'kai@initekconsulting.com', text: 'Kai Klein' },
          { value: 'evan@initekconsulting.com', text: 'Evan Anderson' },
        ],
        inlineUrgencyOptions: [
          { value: null, text: '—' },
          { value: 'low', text: 'Low' },
          { value: 'medium', text: 'Medium' },
          { value: 'high', text: 'High' },
          { value: 'emergency', text: 'Emergency' },
        ],
        inlineStatusOptions: [
          { value: 'new', text: 'New' },
          { value: 'queued', text: 'Queued' },
          { value: 'review', text: 'Under Review' },
          { value: 'progress', text: 'In Progress' },
          { value: 'waiting', text: 'Waiting On Customer' },
          { value: 'testing', text: 'Customer Testing' },
          { value: 'blocked', text: 'Blocked' },
          { value: 'cancelled', text: 'Cancelled' },
          { value: 'completed', text: 'Completed' },
        ],
        inlinePriorityOptions: [
          { value: 'question', text: 'Question/Request' },
          { value: 'minor', text: 'Minor Bug' },
          { value: 'urgent', text: 'Major Bug / System Issue' },
          { value: 'enhancement', text: 'Enhancement Request' },
          { value: 'feature', text: 'New Feature Request' },
        ],
        inlineSaving: {},
        inlineEditing: null,
        inlineDraft: '',
        pokeSaving: {},
        status_options: [
          { value: 'open', text: 'Open'},
          { value: '', text: 'All'},
          { value: 'active', text: 'Active'},
          { value: 'review', text: 'Under Review' },
          { value: 'draft', text: 'Draft (not sent)' },
          { value: 'new', text: 'New' },
          { value: 'queued', text: 'Queued' },
          { value: 'progress', text: 'In Progress' },
          { value: 'waiting', text: 'Waiting On Customer' },
          { value: 'testing', text: 'Customer Testing' },
          { value: 'blocked', text: 'Blocked' },
          { value: 'cancelled', text: 'Cancelled' },
          { value: 'completed', text: 'Completed' },
        ],
        // adminOnly: true marks columns that carry sys-admin-only context
        // (urgency triage, internal target date). filteredFields strips
        // them when effectiveRole !== 'admin', so both real non-admin users
        // and admins previewing via the Muggle toggle see the same trimmed
        // list. updated_by and assigned_to are left visible for everyone —
        // customers can tell at a glance whether their last reply was
        // acknowledged and who is carrying the ticket.
        fields: [
          { key: 'id', label: 'ID', sortable: true },
          { key: 'tenant_id', label: 'Account', sortable: true, thStyle: { width: "200px" } },
          { key: 'priority', label: 'Type', sortable: true },
          { key: 'subject', sortable: true, thStyle: { width: "400px" } },
          { key: 'ticket_status', label: 'Status', sortable: true },
          { key: 'category', label: 'Feature', sortable: true },
          { key: 'urgency', label: 'Urgency', sortable: true, adminOnly: true },
          { key: 'username', label: 'Created By', sortable: true },
          { key: 'created_at', label: 'Created', sortable: true },
          { key: 'updated_by', label: 'Updated By', sortable: true },
          { key: 'updated_at', label: 'Updated', sortable: true },
          // visible to every role. read-only for non-admins: the cell's
          // v-else branch renders a plain avatar/name and only the admin
          // branch mounts the click-to-edit select. note the enforcement is
          // UI-side only -- update_support_ticket restores urgency,
          // internal_notes and billable for non-admin callers but not
          // assigned_to, so the API would accept a hand-crafted write.
          { key: 'assigned_to', label: 'Assigned', sortable: true },
          { key: 'estimated_finish_date', label: 'Target', sortable: true, adminOnly: true },
          // visible to every role — customers see the flag, only admins can
          // move it (the cell disables the box, and the API refuses the write)
          { key: 'billable', label: 'Billable', sortable: true, thClass: 'stl-billable-col', tdClass: 'stl-billable-col' },
          { key: 'actions', label: 'Actions' },
        ],
      }
    },

    async created() {
      // staff start on every customer (the picker narrows); everyone else
      // is scoped to their own account by the api
      this.selected_tenant = this.effectiveRole === 'admin' ? '' : this.user.tenant

      this.load_filters()

      await this.load_data()
    },

    mounted() {
      // a ticket opened in the app-level modal (notification, widget) and
      // closed: patch its row the same way our own modal's close does
      this._ticketChangedHandler = (ref) => this.onTicketModalHidden(ref)
      this.$root.$on('support-ticket-changed', this._ticketChangedHandler)
      this.isMobileView = window.innerWidth <= 768
      this.onWindowResize = () => {
        this.isMobileView = window.innerWidth <= 768
      }
      window.addEventListener('resize', this.onWindowResize)
    },

    beforeDestroy() {
      if (this._ticketChangedHandler) this.$root.$off('support-ticket-changed', this._ticketChangedHandler)
      if (this.onWindowResize) {
        window.removeEventListener('resize', this.onWindowResize)
      }
    },

    methods: {
      // look up the tenant_options entry and return it if is_vip is true.
      // backend (fleet_service.py login) annotates tenant_options with
      // { is_vip, vip_reason } based on fleet size + multi-tenant group.
      // gated on effectiveRole so non-admin users (and Muggle preview) never
      // see VIP signaling — matches the FI-1647 "always effectiveRole, never
      // user.role" rule. funnels chip / row-class / vip_only filter through
      // one bottleneck.
      getTenantVip(tenantId) {
        if (this.effectiveRole !== 'admin') return null
        if (!tenantId || !this.user || !this.user.tenant_options) return null
        const opt = this.user.tenant_options.find(o => o.value === tenantId)
        return (opt && opt.is_vip) ? opt : null
      },
      // row-level class for b-table. tags VIP rows so the left-edge bar renders;
      // tags completed rows so their text fades gray.
      rowClass(item) {
        if (!item) return ''
        const classes = ['stl-row-clickable']
        if (this.getTenantVip(item.tenant_id)) classes.push('stl-row-vip')
        if (item.ticket_status === 'completed') classes.push('stl-row-done')
        return classes.join(' ')
      },
      // compact list timestamp ("4/16 6:39p"); shared with the ops desk
      // list via helpers/dates. full ISO stays on the cell title tooltip.
      formatDateTimeCompact,
      // year-bearing variant ("4/16/25 6:39p") for the created/updated
      // columns, whose rows can span multiple years.
      formatDateTimeCompactYear,
      // target date stored as "MM-DD-YYYY"; shown as "4/16" to match the other
      // compact cells. empty → em-dash so the column visibly conveys "no target".
      formatTargetDateCompact(value) {
        if (!value || typeof value !== 'string') return '—'
        const m = moment(value, 'MM-DD-YYYY', true)
        if (!m.isValid()) return '—'
        return m.format('M/D')
      },
      // shortened labels for the status cell so rows stay single-line.
      // the dropdown filter above still uses the full status_options labels.
      statusShortLabel(value) {
        const shortMap = {
          draft: 'Draft',
          review: 'In Review',
          waiting: 'Waiting',
          // retired status: a row written before it was dropped reads as
          // the lane it now lives in (see support_ticket_board.vue)
          approved: 'Queued',
          testing: 'Testing',
          blocked: 'Blocked',
        }
        return shortMap[value] || this.getOptionText(this.status_options, value)
      },
      // map a priority value to its tag styling. returns null for unknown priorities
      // so the template can fall back to plain text.
      // compactLabel is the short all-caps label rendered in the labeled variant
      // (non-admin view); label is the full tooltip text.
      getPriorityTag(priority) {
        const tags = {
          question:    { label: 'Question',           compactLabel: 'QUESTION',  icon: 'fa-question', variant: 'priority-tag-warning' },
          minor:       { label: 'Minor Bug',          compactLabel: 'MINOR BUG', icon: 'fa-bug',      variant: 'priority-tag-orange-light' },
          urgent:      { label: 'Major Bug / Outage', compactLabel: 'MAJOR BUG', icon: 'fa-bolt',     variant: 'priority-tag-danger' },
          enhancement: { label: 'Enhancement',        compactLabel: 'ENHANCE',   icon: 'fa-wrench',   variant: 'priority-tag-info' },
          feature:     { label: 'New Feature',        compactLabel: 'FEATURE',   icon: 'fa-star',     variant: 'priority-tag-success' },
        }
        return tags[priority] || null
      },
      getUrgencyTag(urgency) {
        const tags = {
          low:       { label: 'Low',       compactLabel: 'LOW',       icon: 'fa-minus',                variant: 'priority-tag-info' },
          medium:    { label: 'Medium',    compactLabel: 'MEDIUM',    icon: 'fa-exclamation',          variant: 'priority-tag-warning' },
          high:      { label: 'High',      compactLabel: 'HIGH',      icon: 'fa-exclamation-triangle', variant: 'priority-tag-orange-light' },
          emergency: { label: 'Emergency', compactLabel: 'EMERGENCY', icon: 'fa-bolt',                 variant: 'priority-tag-danger' },
        }
        const level = urgency || 'low'
        const meta = tags[level] || tags.low
        return {
          ...meta,
          label: urgency
            ? (this.getOptionText(this.inlineUrgencyOptions, urgency) || meta.label)
            : 'Unset (Low)',
        }
      },
      save_filters() {
        const filters = {
          selected_tenant: this.selected_tenant,
          assignee_filter: this.assignee_filter,
          selected_created_by: this.selected_created_by,
          status_preset: this.status_preset,
          type_filters: this.type_filters,
          urgency_filters: this.urgency_filters,
          category_filters: this.category_filters,
          vip_only: this.vip_only,
          billable_only: this.billable_only,
        }
        try { localStorage.setItem('support-filters', JSON.stringify(filters)) } catch (e) { /* private mode */ }
      },
      load_filters() {
        let saved = null
        try { saved = JSON.parse(localStorage.getItem('support-filters') || 'null') } catch (e) { saved = null }
        if (!saved || typeof saved !== 'object') return
        this.auto_reload = false
        if (typeof saved.selected_tenant === 'string') this.selected_tenant = saved.selected_tenant
        if (typeof saved.assignee_filter === 'string') {
          this.assignee_filter = saved.assignee_filter
          this.scope_initialized = true
        } else if (typeof saved.selected_assigned === 'string') {
          // the dropdown this bar replaced
          this.assignee_filter = saved.selected_assigned === 'unassigned' ? UNASSIGNED : saved.selected_assigned
          this.scope_initialized = true
        }
        if (typeof saved.selected_created_by === 'string') this.selected_created_by = saved.selected_created_by
        const status = saved.status_preset !== undefined ? saved.status_preset : saved.selected_status
        if (STATUS_FILTERS.some(o => o.value === status)) this.status_preset = status
        const typeOk = v => TYPE_FILTERS.some(o => o.value === v)
        const urgencyOk = v => URGENCY_FILTERS.some(o => o.value === v)
        const featureOk = v => this.feature_filters.some(o => o.value === v)
        if (Array.isArray(saved.type_filters)) this.type_filters = saved.type_filters.filter(typeOk)
        else if (typeOk(saved.selected_priority)) this.type_filters = [saved.selected_priority]
        if (Array.isArray(saved.urgency_filters)) this.urgency_filters = saved.urgency_filters.filter(urgencyOk)
        else if (urgencyOk(saved.selected_urgency)) this.urgency_filters = [saved.selected_urgency]
        if (Array.isArray(saved.category_filters)) this.category_filters = saved.category_filters.filter(featureOk)
        else if (featureOk(saved.selected_category)) this.category_filters = [saved.selected_category]
        this.vip_only = !!saved.vip_only
        this.billable_only = !!saved.billable_only
        this.auto_reload = true
      },
      toggle_filter(name, value) {
        const list = this[name]
        this[name] = list.includes(value) ? list.filter(v => v !== value) : [...list, value]
      },
      // a chip that is already selected turns the scope back to everyone
      toggle_assignee(value) {
        this.assignee_filter = this.assignee_filter === value ? '' : value
      },
      is_team_chip_selected(member) {
        if (this.assignee_filter === member.username) return true
        return this.assignee_filter === MINE && this.user && this.user.username === member.username
      },
      // the strip's × on one pill
      remove_filter_pill(pill) {
        if (Array.isArray(this[pill.field])) this.toggle_filter(pill.field, pill.value)
        else if (typeof this[pill.field] === 'boolean') this[pill.field] = false
        else this[pill.field] = ''
      },
      clear_filters() {
        this.selected_tenant = ''
        this.assignee_filter = ''
        this.selected_created_by = ''
        this.status_preset = ''
        this.type_filters = []
        this.urgency_filters = []
        this.category_filters = []
        this.vip_only = false
        this.billable_only = false
        this.search_text = ''
      },
      async load_data() {

        this.loading = true
        try {
          // '' for the API's `subject` argument, deliberately. That filter is
          // Attr('subject').contains(...) -- subject-only and case-sensitive --
          // so passing search_text there would drop the very rows the widened
          // client-side match is meant to find (a ticket assigned to Kai whose
          // subject never says "kai"). All free-text matching is client-side in
          // filter_results(), which is also what the keystroke watcher runs.
          // blank startDate/endDate mean "every date" -- the API treats the
          // range as optional and omits the updated_at filter entirely.
          // staff ask for every customer and narrow by customer on the client
          // (client_filter), so the Customer picker can count what each one
          // has. the api's tenant clause was a post-scan filter anyway.
          // everyone else's query stays scoped to their own account.
          const tenantArg = this.effectiveRole === 'admin' ? '' : this.selected_tenant
          // every status, every assignee, every date: the chips narrow
          const {value: {data = []}} = await search_support_tickets(
              tenantArg, '', '', '', '', '', '', '', ''
          )
          this.items_full = data || []
          // a first visit opens on the viewer's own tickets (staff)
          if (!this.scope_initialized) {
            this.assignee_filter = this.effectiveRole === 'admin' ? MINE : ''
            this.scope_initialized = true
          }
          this.filter_results()
          this.save_filters()

        } catch (e) {
          this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
        }
        this.loading = false
      },
      async delete_support_ticket(tenant_id, id) {
        if (confirm("Are you sure you want to delete this ticket?")) {
          this.$emit('showAlert', "deleting ticket", 'info')
          const result = await delete_support_ticket(tenant_id, id)
          this.$emit('showAlert', "support ticket has been deleted", 'success', 5000)
          await this.load_data()
        }
      },
      pokeSaveKey(ticket) {
        return `${ticket.tenant_id}-${ticket.id}`
      },
      isPokeSaving(ticket) {
        return !!this.pokeSaving[this.pokeSaveKey(ticket)]
      },
      async pokeTicket(ticket) {
        if (!this.user || this.user.role !== 'admin' || this.isPokeSaving(ticket)) return
        const saveKey = this.pokeSaveKey(ticket)
        this.$set(this.pokeSaving, saveKey, true)
        try {
          const { value: { data = {} } } = await poke_support_ticket(ticket.tenant_id, ticket.id)
          const emailed = Number(data.emailed || 0)
          this.$emit('showAlert', data.message || 'Update email sent', emailed ? 'success' : 'warning', 5000)
        } catch (e) {
          this.$emit('showAlert', 'Error sending update email', 'danger', 5000)
        } finally {
          this.$delete(this.pokeSaving, saveKey)
        }
      },
      getOptionText(options, value) {
        const option = options.find(opt => opt.value === value)
        return option ? option.text : value
      },
      displayUserName(username, displayNameOverride) {
        if (displayNameOverride) return displayNameOverride
        if (!username) return ''
        const fromInlineAssigned = this.getOptionText(this.inlineAssignedOptions, username)
        if (fromInlineAssigned && fromInlineAssigned !== username && fromInlineAssigned !== '—') {
          return fromInlineAssigned
        }
        const fromAssigned = this.getOptionText(this.assigned_options, username)
        if (fromAssigned && fromAssigned !== username && fromAssigned !== 'All') {
          return fromAssigned
        }
        return String(username).split('@')[0]
      },
      initekTeamImage(username) {
        return initekTeamImageForUsername(username)
      },
      // the free-text box matches what the row actually SHOWS, not just the
      // raw stored value -- typing "Kai Klein", "Driver Manager" or an account
      // name finds the same rows the eye would. each entry below mirrors the
      // display expression of its column template; the raw value is kept
      // alongside so an email address or a tenant_id still matches.
      searchableText(item) {
        const tenantOptions = (this.user && this.user.tenant_options) || []
        return [
          item.subject,
          item.description,
          // Account
          item.tenant_id,
          item.tenant_id ? this.getOptionText(tenantOptions, item.tenant_id) : null,
          // Feature. guarded on a truthy value because both option arrays
          // lead with the filter dropdown's {value: '', text: 'All'} sentinel
          // -- an unguarded lookup would let a search for "all" match every
          // ticket that has no category.
          item.category,
          item.category ? this.getOptionText(this.category_options, item.category) : null,
          // Created By
          item.username,
          item.created_by_name,
          // Updated By
          item.updated_by,
          item.updated_by ? this.displayUserName(item.updated_by, item.updated_by_name) : null,
          // Assigned -- 'Unassigned' is the literal fallback the cell renders,
          // so searching for it finds the tickets nobody has picked up.
          item.assigned_to,
          this.getOptionText(this.inlineAssignedOptions, item.assigned_to) || 'Unassigned',
        ]
      },
      filter_results() {
        this.items = this.client_filter(this.items_full)
      },
      // the client-side narrowing, as one pipeline. `skip` leaves one
      // dimension out, which is how the Created By picker counts what each
      // creator would leave without its own pick collapsing the list.
      client_filter(rows, skip = '') {
        const admin = this.effectiveRole === 'admin'
        if (admin) {
          if (skip !== 'tenant' && this.selected_tenant)
            rows = rows.filter(item => item.tenant_id === this.selected_tenant)
          if (skip !== 'assignee') {
            if (this.assignee_filter === UNASSIGNED) rows = rows.filter(item => !item.assigned_to)
            else if (this.assignee_filter === MY_CREATED) {
              // My Created reads the "Created By" user (the requester, stored
              // on username), not the assigned-or-requester scope Mine uses
              const me = this.user && this.user.username
              rows = rows.filter(item => !!me && item.username === me)
            } else if (this.assignee_filter) {
              // Mine and the photo chips read the person in any role: assigned
              // to them, or opened by them (Ben, 2026-09-25)
              const target = this.assignee_filter === MINE ? (this.user && this.user.username) : this.assignee_filter
              rows = rows.filter(item => ticketInvolves(item, target))
            }
          }
          if (skip !== 'created_by' && this.selected_created_by && this.selected_created_by !== CREATED_BY_SEPARATOR)
            rows = rows.filter(item => item.username === this.selected_created_by)
          if (skip !== 'urgency' && this.urgency_filters.length)
            rows = rows.filter(item => this.urgency_filters.includes(item.urgency || 'low'))
          if (skip !== 'vip' && this.vip_only)
            rows = rows.filter(item => !!this.getTenantVip(item.tenant_id))
          // absent on tickets that predate the flag, hence the truthiness test
          if (skip !== 'billable' && this.billable_only)
            rows = rows.filter(item => !!item.billable)
        }
        if (skip !== 'status' && this.status_preset)
          rows = rows.filter(item => this.statusMatchesFilter(item.ticket_status))
        if (skip !== 'type' && this.type_filters.length)
          rows = rows.filter(item => this.type_filters.includes(item.priority))
        if (skip !== 'feature' && this.category_filters.length)
          rows = rows.filter(item => this.category_filters.includes(item.category))
        if (skip !== 'search' && this.search_text)
          rows = rows.filter(item => this.searchableText(item).some(value => this.contains(value, this.search_text)))
        return rows
      },
      // one chip row's counts: rows that survive every OTHER filter, keyed
      // by the value each row carries for this dimension
      count_by(skip, keyOf) {
        const counts = {}
        this.client_filter(this.items_full, skip).forEach(item => {
          const k = keyOf(item)
          if (k === undefined || k === null) return
          counts[k] = (counts[k] || 0) + 1
        })
        return counts
      },
      // String() rather than a bare .toLowerCase(): the haystack now carries
      // whatever the record holds, and one non-string value would otherwise
      // throw on every keystroke and blank the list.
      contains(val1, val2) {
        if (val1 != null) {
          return String(val1).toLowerCase().indexOf(String(val2).toLowerCase()) != -1;
        }
        return false;
      },
      sortItems(items, sortBy, sortDesc) {
        if (!items || !items.length || !sortBy) {
          return items ? [...items] : []
        }
        const direction = sortDesc ? -1 : 1
        const compare = (a, b) => {
          let av = a[sortBy]
          let bv = b[sortBy]

          if (sortBy === 'id') {
            return (Number(av) - Number(bv)) * direction
          }
          if (sortBy === 'urgency') {
            av = URGENCY_RANK[av] || URGENCY_RANK.low
            bv = URGENCY_RANK[bv] || URGENCY_RANK.low
            const urgencyDiff = (av - bv) * direction
            if (urgencyDiff !== 0) return urgencyDiff
            const updatedDiff = (b.updated_at || '').localeCompare(a.updated_at || '')
            if (updatedDiff !== 0) return updatedDiff
            return Number(b.id) - Number(a.id)
          }
          if (sortBy === 'priority') {
            av = PRIORITY_RANK[av] || 0
            bv = PRIORITY_RANK[bv] || 0
            return (av - bv) * direction
          }
          // billable is tri-state on disk (true / false / absent on tickets
          // that predate the field). collapse to 0/1 up front so absent and
          // explicit-false sort together instead of the missing ones being
          // shoved to the bottom by the null handling below.
          if (sortBy === 'billable') {
            const diff = ((av ? 1 : 0) - (bv ? 1 : 0)) * direction
            return diff !== 0 ? diff : Number(b.id) - Number(a.id)
          }

          const aNull = av == null || av === ''
          const bNull = bv == null || bv === ''
          if (aNull && bNull) return Number(b.id) - Number(a.id)
          if (aNull) return 1
          if (bNull) return -1

          if (typeof av === 'number' && typeof bv === 'number') {
            const diff = (av - bv) * direction
            return diff !== 0 ? diff : Number(b.id) - Number(a.id)
          }

          const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' })
          return cmp !== 0 ? cmp * direction : Number(b.id) - Number(a.id)
        }
        return [...items].sort(compare)
      },
      inlineSaveKey(ticket, field = '') {
        return `${ticket.tenant_id}-${ticket.id}${field ? '-' + field : ''}`
      },
      inlineEditKey(ticket, field) {
        return `${ticket.tenant_id}-${ticket.id}:${field}`
      },
      isInlineEditing(ticket, field) {
        return this.inlineEditing === this.inlineEditKey(ticket, field)
      },
      startInlineEdit(ticket, field, event) {
        if (this.effectiveRole !== 'admin' || this.isInlineSaving(ticket, field)) return
        if (event && event.stopPropagation) event.stopPropagation()
        this.inlineEditing = this.inlineEditKey(ticket, field)
        if (field === 'subject') {
          this.inlineDraft = ticket.subject || ''
        }
      },
      clearInlineEdit() {
        this.inlineEditing = null
        this.inlineDraft = ''
      },
      commitInlineSubject(ticket) {
        if (!this.isInlineEditing(ticket, 'subject')) return
        const trimmed = (this.inlineDraft || '').trim()
        const current = (ticket.subject || '').trim()
        if (!trimmed || trimmed === current) {
          this.clearInlineEdit()
          return
        }
        this.updateTicketInline(ticket, 'subject', trimmed)
      },
      onInlineSubjectBlur(ticket) {
        setTimeout(() => {
          if (!this.isInlineEditing(ticket, 'subject') || this.isInlineSaving(ticket, 'subject')) return
          this.commitInlineSubject(ticket)
        }, 120)
      },
      onInlineEditBlur(ticket, field) {
        setTimeout(() => {
          if (this.isInlineEditing(ticket, field) && !this.isInlineSaving(ticket, field)) {
            this.clearInlineEdit()
          }
        }, 120)
      },
      isInlineSaving(ticket, field = '') {
        if (field) {
          return !!this.inlineSaving[this.inlineSaveKey(ticket, field)]
        }
        return Object.keys(this.inlineSaving).some(key => key.startsWith(this.inlineSaveKey(ticket)))
      },
      openTicketModal(item) {
        this.$refs.ticketModal.open(item)
      },
      openCreate(prefill = null) {
        this.$refs.createModal.open(prefill)
      },
      async onTicketCreated(created) {
        await this.load_data()
        if (created && created.attachmentFailures > 0) this.openTicketModal(created)
      },
      // the full-page deep link, the shape help_center opens in detail mode
      // and the backend's email/Slack links use. tenant_id is a lookup hint.
      ticketHref(item) {
        return this.$router.resolve({
          name: 'help_center',
          query: { tab: 'support', ticket_id: String(item.id), tenant_id: item.tenant_id },
        }).href
      },
      // b-table row-clicked. the row is one big tap target for the modal,
      // but the cells that do something else on click keep it: the
      // click-to-edit cells (admins), the billable checkbox, the id link
      // and the action buttons. a click that ends a text selection is a
      // selection, not a request to open.
      onRowClicked(item, index, event) {
        const target = event && event.target
        if (target && target.closest) {
          if (target.closest('a, button, input, select, textarea, label, .custom-control, .stl-inline-select, .stl-inline-input')) return
          if (this.effectiveRole === 'admin' && target.closest('.stl-inline-readonly')) return
        }
        if (this.inlineEditing) return
        if (window.getSelection && String(window.getSelection()).length) return
        this.openTicketModal(item)
      },
      // the list's status dropdown, as a predicate: the same groups the
      // api's ticket_status argument expands to in search_support_tickets
      statusMatchesFilter(status) {
        const f = this.status_preset
        if (!f) return true
        if (STATUS_GROUPS[f]) return STATUS_GROUPS[f].includes(status)
        return status === f
      },
      // whatever happened in the modal is on the server now: refetch that
      // one ticket and patch its row, drop it if it was deleted or moved
      // out of the status filter, rather than rescanning the whole list
      async onTicketModalHidden(ref) {
        if (!ref) return
        try {
          const { value: { data = {} } = {} } = await get_support_ticket(ref.tenant_id, ref.id) || {}
          const item = data.item
          const keep = item && item.tenant_id === ref.tenant_id && this.statusMatchesFilter(item.ticket_status)
          if (keep) {
            this.patchTicketInLists(ref.tenant_id, ref.id, item)
          } else {
            const drop = list => {
              const idx = list.findIndex(t => t.tenant_id === ref.tenant_id && Number(t.id) === Number(ref.id))
              if (idx >= 0) list.splice(idx, 1)
            }
            drop(this.items)
            drop(this.items_full)
          }
        } catch (e) {
          // the row is stale at worst; Search reloads
        }
      },
      patchTicketInLists(tenantId, id, updates) {
        const apply = (list) => {
          const idx = list.findIndex(t => t.tenant_id === tenantId && Number(t.id) === Number(id))
          if (idx >= 0) {
            Object.assign(list[idx], updates)
          }
        }
        apply(this.items)
        apply(this.items_full)
      },
      async updateTicketInline(ticket, field, value) {
        if (this.effectiveRole !== 'admin') return
        let normalized = value === '' ? null : value
        if (field === 'subject') {
          normalized = (value || '').trim()
          if (!normalized) {
            this.clearInlineEdit()
            return
          }
        }
        // boolean columns coerce to real booleans and compare on
        // truthiness. the generic `|| null` no-op check below would treat
        // false and "absent" as the same value, which would silently drop
        // the very first save that turns a legacy ticket's flag off.
        if (field === 'billable') {
          normalized = !!value
          if (!!ticket[field] === normalized) {
            this.clearInlineEdit()
            return
          }
        } else if ((ticket[field] || null) === (normalized || null)) {
          this.clearInlineEdit()
          return
        }

        const saveKey = this.inlineSaveKey(ticket, field)
        if (this.inlineSaving[saveKey]) return
        this.$set(this.inlineSaving, saveKey, true)

        try {
          const { value: { data = {} } } = await get_support_ticket(ticket.tenant_id, ticket.id)
          if (!data.item) {
            throw new Error('ticket not found')
          }
          const model = { ...data.item, new_tenant_id: data.item.tenant_id }
          model[field] = normalized
          await update_support_ticket(ticket.tenant_id, model, false)
          this.patchTicketInLists(ticket.tenant_id, ticket.id, { [field]: normalized })
        } catch (e) {
          this.$emit('showAlert', 'Error updating ticket, try again or open the ticket to edit', 'danger', 5000)
        } finally {
          this.$delete(this.inlineSaving, saveKey)
          this.clearInlineEdit()
        }
      },
    },

    computed: {
      ...mapState({
        user: state => state.profile.user
      }),
      filteredFields() {
        const isAdminView = this.effectiveRole === 'admin'
        return this.fields.filter(field => {
          // sys-admin-only columns disappear in non-admin views (real
          // customers and the Muggle preview alike).
          if (field.adminOnly && !isAdminView) {
            return false;
          }
          if ((!isAdminView && this.user.tenant_options.length === 1) && field.key === 'tenant_id') {
            return false;
          }
          return true;
        })
      },
      // counts the dimensions folded behind the mobile Filters toggle, so a
      // phone can tell at a glance that the list is narrowed. search_text is
      // excluded — it keeps its own always-visible field.
      activeFilterCount() {
        let n = 0
        if (this.status_preset) n++
        if (this.type_filters.length) n++
        if (this.category_filters.length) n++
        if (this.effectiveRole === 'admin') {
          if (this.selected_tenant) n++
          if (this.assignee_filter) n++
          if (this.urgency_filters.length) n++
          if (this.vip_only) n++
          if (this.billable_only) n++
          if (this.selected_created_by) n++
        }
        return n
      },
      // ---- chip rows: vocabulary and counts (mirrors the board) ----
      feature_filters() {
        return this.category_options.filter(o => o.value)
      },
      team_chips() {
        return INITEK_TEAM_FILTER_CHIPS.map(m => ({ ...m, username: m.usernames[0] }))
      },
      assignee_counts() {
        const rows = this.client_filter(this.items_full, 'assignee')
        const me = this.user && this.user.username
        const counts = { '': rows.length, [MINE]: 0, [MY_CREATED]: 0, [UNASSIGNED]: 0 }
        rows.forEach(item => {
          if (!item.assigned_to) counts[UNASSIGNED] += 1
          ticketPeople(item).forEach(p => { counts[p] = (counts[p] || 0) + 1 })
          if (ticketInvolves(item, me)) counts[MINE] += 1
          if (me && item.username === me) counts[MY_CREATED] += 1
        })
        return counts
      },
      status_counts() {
        const rows = this.client_filter(this.items_full, 'status')
        const counts = {}
        STATUS_FILTERS.forEach(o => {
          counts[o.value] = o.value === ''
            ? rows.length
            : rows.filter(item => STATUS_GROUPS[o.value]
              ? STATUS_GROUPS[o.value].includes(item.ticket_status)
              : item.ticket_status === o.value).length
        })
        return counts
      },
      type_counts() {
        return this.count_by('type', item => item.priority)
      },
      urgency_counts() {
        return this.count_by('urgency', item => item.urgency || 'low')
      },
      feature_counts() {
        return this.count_by('feature', item => item.category)
      },
      flag_counts() {
        return {
          vip: this.client_filter(this.items_full, 'vip').filter(item => !!this.getTenantVip(item.tenant_id)).length,
          billable: this.client_filter(this.items_full, 'billable').filter(item => !!item.billable).length,
        }
      },
      // the status chips worth showing: those with a row behind them, plus
      // the pressed one; the first three of those show until expanded
      available_status_filters() {
        return STATUS_FILTERS.filter(o => this.status_counts[o.value] || this.status_preset === o.value)
      },
      shown_status_filters() {
        if (this.status_expanded) return this.available_status_filters
        return this.available_status_filters.filter((o, i) => i < FEATURE_CHIPS_SHOWN || this.status_preset === o.value)
      },
      hidden_status_count() {
        return this.available_status_filters.length - this.shown_status_filters.length
      },
      available_feature_filters() {
        return this.feature_filters.filter(o => this.feature_counts[o.value] || this.category_filters.includes(o.value))
      },
      shown_feature_filters() {
        if (this.features_expanded) return this.available_feature_filters
        return this.available_feature_filters.filter((o, i) => i < FEATURE_CHIPS_SHOWN || this.category_filters.includes(o.value))
      },
      hidden_feature_count() {
        return this.available_feature_filters.length - this.shown_feature_filters.length
      },
      // every narrowing control that is set, as a removable pill. the
      // status one-of counts as a filter here (it is one on the list).
      active_filter_pills() {
        const pills = []
        const add = (key, dim, text, field, value) => pills.push({ key, dim, text, field, value })
        const tenantOptions = (this.user && this.user.tenant_options) || []
        const admin = this.effectiveRole === 'admin'
        // in the order the controls sit on screen, left to right, row by
        // row -- the same arrangement as the board
        if (admin) {
          if (this.assignee_filter === MINE) add('assignee', 'Person', 'Me', 'assignee_filter')
          else if (this.assignee_filter === MY_CREATED) add('assignee', 'Created', 'By me', 'assignee_filter')
          else if (this.assignee_filter === UNASSIGNED) add('assignee', 'Assigned', 'Unassigned', 'assignee_filter')
          else if (this.assignee_filter) add('assignee', 'Person', this.displayUserName(this.assignee_filter), 'assignee_filter')
        }
        if (this.status_preset) {
          const opt = STATUS_FILTERS.find(o => o.value === this.status_preset)
          add('status', 'Status', opt ? opt.text : this.status_preset, 'status_preset')
        }
        if (admin) {
          this.urgency_filters.forEach(v => {
            const opt = URGENCY_FILTERS.find(o => o.value === v)
            add('urgency-' + v, 'Urgency', opt ? opt.text : v, 'urgency_filters', v)
          })
          if (this.vip_only) add('vip', 'Flag', 'VIP', 'vip_only')
          if (this.billable_only) add('billable', 'Flag', 'Billable', 'billable_only')
        }
        if (this.search_text.trim()) add('search', 'Search', `“${this.search_text.trim()}”`, 'search_text')
        this.type_filters.forEach(v => {
          const opt = TYPE_FILTERS.find(o => o.value === v)
          add('type-' + v, 'Type', opt ? opt.text : v, 'type_filters', v)
        })
        this.category_filters.forEach(v => add('feature-' + v, 'Feature', this.getOptionText(this.category_options, v), 'category_filters', v))
        if (admin) {
          if (this.selected_tenant) add('tenant', 'Customer', this.getOptionText(tenantOptions, this.selected_tenant) || this.selected_tenant, 'selected_tenant')
          if (this.selected_created_by && this.selected_created_by !== CREATED_BY_SEPARATOR) {
            add('createdBy', 'Created by', this.displayUserName(this.selected_created_by), 'selected_created_by')
          }
        }
        return pills
      },
      // admins can opt into a "view as non-admin" preview via the navbar toggle;
      // everywhere we gate UI on admin-ness, use this instead of user.role so
      // the preview toggle actually affects the render.
      effectiveRole() {
        const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
        if (this.user && this.user.role === 'admin' && viewAs === 'non-admin') return 'customer'
        return this.user && this.user.role
      },
      // customers with a ticket in the loaded set (after every other
      // client-side filter), each with its count; the current pick stays
      // listed even at zero so the select never blanks while filtering
      search_tenant_options() {
        const rows = this.client_filter(this.items_full, 'tenant')
        const counts = {}
        rows.forEach(item => { if (item.tenant_id) counts[item.tenant_id] = (counts[item.tenant_id] || 0) + 1 })
        if (this.selected_tenant && !counts[this.selected_tenant]) counts[this.selected_tenant] = 0
        const tenantOptions = (this.user && this.user.tenant_options) || []
        const name = id => this.getOptionText(tenantOptions, id) || id
        const options = Object.keys(counts)
          .map(id => ({ value: id, text: `${name(id)} (${counts[id]})`, name: name(id) }))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ value, text }) => ({ value, text }))
        return [{ value: '', text: `All (${rows.length})` }, ...options]
      },
      // the Created By picker: the Initek roster pinned above a separator,
      // then every other creator, each with the count of rows it would
      // leave (rows surviving every other client-side filter). the current
      // pick stays listed at zero rather than vanishing from under itself.
      created_by_options() {
        const rows = this.client_filter(this.items_full, 'created_by')
        const counts = {}
        const names = {}
        rows.forEach(item => {
          if (!item.username) return
          counts[item.username] = (counts[item.username] || 0) + 1
          names[item.username] = names[item.username] || this.displayUserName(item.username, item.created_by_name)
        })
        if (this.selected_created_by && this.selected_created_by !== '__sep__' && !counts[this.selected_created_by]) {
          counts[this.selected_created_by] = 0
          names[this.selected_created_by] = this.displayUserName(this.selected_created_by)
        }
        const initekUsernames = new Set([].concat(...INITEK_TEAM_MEMBERS.map(m => m.usernames)))
        const option = u => ({ value: u, text: `${names[u]} (${counts[u]})` })
        const byName = (a, b) => names[a].localeCompare(names[b])
        const initek = Object.keys(counts).filter(u => initekUsernames.has(u)).sort(byName)
        const others = Object.keys(counts).filter(u => !initekUsernames.has(u)).sort(byName)
        const options = [{ value: '', text: `Anyone (${rows.length})` }]
        initek.forEach(u => options.push(option(u)))
        if (initek.length && others.length) {
          options.push({ value: '__sep__', text: '──────────', disabled: true })
        }
        others.forEach(u => options.push(option(u)))
        return options
      },
      // mirror of b-table's current-page slicing so the mobile card list
      // paginates the same way as the desktop table; both views bind to
      // currentPage + perPage so the pagination control affects both.
      paginatedItems() {
        const start = (this.currentPage - 1) * this.perPage
        return this.sortedItems.slice(start, start + this.perPage)
      },
      sortedItems() {
        return this.sortItems(this.items, this.sortBy, this.sortDesc)
      },
    },

    watch: {
      support_ticket: {
        deep: true,
        handler() {
          this.load_data()
        }
      },
      // every chip and picker is a client-side narrowing: re-filter and
      // remember. (search is not remembered.)
      vip_only() { this.filter_results(); this.save_filters() },
      billable_only() { this.filter_results(); this.save_filters() },
      selected_created_by() { this.filter_results(); this.save_filters() },
      assignee_filter() { this.filter_results(); this.save_filters() },
      status_preset() { this.filter_results(); this.save_filters() },
      type_filters() { this.filter_results(); this.save_filters() },
      urgency_filters() { this.filter_results(); this.save_filters() },
      category_filters() { this.filter_results(); this.save_filters() },
      search_text() { this.filter_results() },
      selected_tenant() {
        if (!this.auto_reload) return
        if (this.effectiveRole === 'admin') {
          this.filter_results()
          this.save_filters()
        } else {
          this.load_data()
        }
      },
    }
  }
</script>

<style scoped>
/* horizontal padding kept minimal so the table + pagination span the full page
   width — prior 20px side padding + a nested 20px-margin wrapper were eating
   ~80px per side. vertical padding stays for visual breathing room under navbar */
.stl-root {
  /* no top padding: the page header rail above already provides the
     separation, and this page's own 10px stacked on top of the filter
     row's margin put ~20px of dead space above the first control. */
  padding: 0 0 10px;
}

/* wrapper around the b-table + pagination. vertical margin only so the table
   tracks the page edges. replaces the prior inline `margin:20px`.
   the top margin is the gap between the controls and the results, and it
   lives here rather than on the filter rows so it holds either way: the
   second (admin) filter row isn't there for customers, and margins collapse
   to whichever is larger. */
.stl-table-wrap {
  margin: 16px 0 20px;
}

/* mobile card list — only rendered when isMobileView=true (≤768px).
   each card opens the ticket modal (it used to navigate to the detail page),
   matching the list→detail pattern used by the help articles tab
   (same 768px cut-off). card shows: id + priority tag, subject,
   status/tenant/updated meta row, and (admin only) the assignee. */
.stl-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* padding, not margin, so it can't collapse into the filter row's —
     matches the desktop table's separation from the controls above. */
  padding: 12px 10px 16px;
}
.stl-mobile-card {
  cursor: pointer;
  display: block;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.12s ease;
}
.stl-mobile-card:hover,
.stl-mobile-card:focus {
  background: var(--fi-brand-tint);
  text-decoration: none;
  color: inherit;
}
.stl-mobile-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.stl-mobile-card-id {
  font-size: 12px;
  color: #888;
  font-weight: 600;
}
.stl-mobile-card-subject {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  word-break: break-word;
}
.stl-mobile-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}
.stl-mobile-card-sep { color: #ccc; }
.stl-mobile-card-admin {
  font-size: 11px;
  color: #777;
  margin-top: 4px;
}
/* only rendered when the flag is set, so it reads as a positive marker
   on the card rather than a field that's usually blank. */
.stl-mobile-card-billable {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 9px;
  background: #eaf5ea;
  color: #2f6b34;
  font-weight: 600;
  white-space: nowrap;
}
.stl-mobile-card--muted { opacity: 0.55; }

/* skeleton variant of the mobile card. inert (no hover/cursor) so the
   user can't tap into a half-loaded ticket. shimmer animation runs on
   the bars themselves; card frame stays static. */
.stl-mobile-card--skeleton {
  pointer-events: none;
  cursor: default;
  background: #fff;
}
.stl-mobile-card--skeleton:hover { background: #fff; }
.stl-skeleton {
  display: inline-block;
  background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: stl-skeleton-shimmer 1.4s ease-in-out infinite;
}
@keyframes stl-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.stl-skeleton--id      { width: 38px; height: 12px; }
.stl-skeleton--tag     { width: 86px; height: 20px; border-radius: 4px; }
.stl-skeleton--subject { display: block; width: 90%; height: 15px; margin: 8px 0 0; }
.stl-skeleton--subject-2 { width: 60%; margin-top: 4px; }
.stl-skeleton-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.stl-skeleton--meta-status { width: 60px; height: 11px; }
.stl-skeleton--meta-tenant { width: 100px; height: 11px; }
.stl-skeleton--meta-time   { width: 50px; height: 11px; }
.stl-skeleton--admin {
  display: block;
  width: 80px;
  height: 11px;
  margin-top: 8px;
}
.stl-mobile-card--vip { border-left: 3px solid #b58100; }
.stl-mobile-empty {
  color: #888;
  font-size: 13px;
  text-align: center;
  padding: 20px 10px;
}
.stl-mobile-card-tenant.is-vip {
  color: #7a5400;
  font-weight: 600;
}

/* inner wrapper gives the table a small breath of space from the page edges
   without affecting the pagination row below, which stays flush-wide and
   centers its own controls via b-pagination `align="center"`. */
.stl-table-inner {
  margin: 0 10px;
}

/* mobile filters toggle — hidden on desktop, where every control has room */
.stl-mobile-only { display: none; }
.stl-filter-toggle {
  align-items: center;
  gap: 7px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 7px 14px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--fi-text, #374151);
  cursor: pointer;
  transition: background .12s, border-color .12s;
}
.stl-filter-toggle:hover { background: var(--fi-surface-sunken, #eef4fb); }
.stl-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
.stl-filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

@media (max-width: 768px) {
  .stl-status-shortcuts,
  .stl-name-shortcuts {
    display: none;
  }
  /* mobile-only affordances turn on here */
  .stl-mobile-only { display: inline-flex; }
  /* the toggle hugs the left edge rather than riding the row's centring, so
     it sits where a toolbar control is looked for. the auto margin also
     pushes the create button out to the right edge, which is what leaves the
     collapsed row reading as [Filters] ......... [+]. */
  .stl-root .stl-filter-toggle { margin-right: auto; }
  /* on a phone the search box gets a row to itself: order pushes it past the
     create button and the full flex-basis forces the wrap, so the collapsed
     view reads [Filters] ... [+] over a full-width search field. same flex
     container, so no duplicate input and no JS breakpoint. */
  .stl-root .stl-search-slot {
    order: 99;
    flex: 0 0 100%;
  }
  .stl-root .stl-fbar-input { width: 100%; }
  /* collapsed: every dimension folds away — the three row-1 selects, the
     Search button and the whole admin row — leaving the free-text box and
     the create action. the toggle above flips it back and its badge counts
     what is hidden. */
  .stl-filters-collapsed .stl-fbar-collapsible,
  .stl-filters-collapsed .stl-filter-row {
    display: none;
  }
  /* Create Ticket collapses to a bare + so the filter row keeps the date
     range and Search on one line on a phone. !important because the icon
     carries an inline display:none that a plain rule can't outrank. */
  .stl-root .stl-create-btn {
    padding-left: 10px;
    padding-right: 10px;
  }
  .stl-root .stl-create-btn .stl-create-label-full {
    display: none;
  }
  .stl-root .stl-create-btn .stl-create-icon-only {
    display: inline !important;
    color: #fff;
    font-size: 16px;
  }
}

</style>

<style>
/* sort indicators inline instead of floating above — use simple descendant
   selectors so they work regardless of BV's sticky-header wrapper structure */
.stl-root th[aria-sort] {
  background-image: none !important;
  padding-right: 0.75rem !important;
  white-space: nowrap;
}

/* hide BV's default ::before sort icons */
.stl-root th[aria-sort]::before {
  display: none !important;
}

.stl-root th[aria-sort]::after {
  display: inline-block !important;
  margin-left: 4px;
  font-size: 10px;
  color: #999;
  vertical-align: middle;
  float: none !important;
  width: auto !important;
  height: auto !important;
  opacity: 1 !important;
}

.stl-root th[aria-sort="none"]::after {
  content: "\2195" !important;
}

.stl-root th[aria-sort="ascending"]::after {
  content: "\25B2" !important;
  color: var(--fi-rail-accent);
}

.stl-root th[aria-sort="descending"]::after {
  content: "\25BC" !important;
  color: var(--fi-rail-accent);
}

/* compact table: remove vertical borders, keep horizontal rules */
.stl-root .table.b-table {
  font-size: 13px;
  border-collapse: collapse;
}

.stl-root .table.b-table th,
.stl-root .table.b-table td {
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  /* !important needed because 15+ unscoped component stylesheets globally
     set '.table td { padding: 0.1rem !important }' (a long-standing leak from
     non-scoped Vue <style> blocks in other components). */
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
/* first/last cells hug the page edges a little more so the table visually
   reads as full-width without the content feeling cramped against the edge */
.stl-root .table.b-table th:first-child,
.stl-root .table.b-table td:first-child {
  padding-left: 12px !important;
}
.stl-root .table.b-table th:last-child,
.stl-root .table.b-table td:last-child {
  padding-right: 12px !important;
}

.stl-root .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  /* sticky column headers — stay pinned to viewport top while the page scrolls,
     without introducing a nested scroll container on the table itself */
  position: sticky;
  top: 0;
  z-index: 2;
  /* box-shadow draws the divider line instead of border-bottom.
     needed because:
       1. border-bottom disappears on sticky <th> when border-collapse: collapse
       2. ::after is already used by the sort-arrow rules
     shadow stays fixed to the th through sticky positioning. */
  box-shadow: inset 0 -2px 0 0 #ccc;
}

.stl-root .table.b-table tbody tr:hover td {
  background-color: var(--fi-brand-tint);
}

/* priority tags — muted pastel palette matching the callout design language.
   see feedback_tone_and_callouts.md: gentle, informational, not alarming.
   to be rolled into the shared Callout Panel primitive during NC Phase 2. */
.stl-root .priority-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  /* line-height: 1 everywhere inside the pill so the icon box and the
     label box have the same height as their glyphs — otherwise an
     inherited container line-height on the <i> would push it off the
     label's optical baseline (flex `align-items: center` aligns the
     box centers, not the glyph centers). */
  line-height: 1;
  border-radius: 4px;
  border: 1px solid;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
/* icon-only variant used in the admin list view — label moves to title tooltip
   so the cell stays narrow. the full-text variant is still used in ticket
   details and the non-admin list view (priority-tag--labeled).
   fixed icon width keeps every box the same compact size regardless of which
   FA glyph is inside (otherwise narrow glyphs like `?` render a tighter box
   than wider ones like bug/wrench/star). */
.stl-root .priority-tag--icon-only {
  padding: 3px 4px;
  gap: 0;
  cursor: help;
  line-height: normal;
  align-items: center;
  justify-content: center;
}
.stl-root .priority-tag .fa {
  font-size: 11px;
  line-height: 1;
}
.stl-root .priority-tag--icon-only .fa {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  width: 12px;
  height: 13px;
  line-height: 1;
  text-align: center;
  overflow: visible;
}
/* labeled variant: icon + all-caps compact label. more prominent than the
   icon-only column so non-admins can scan priority without needing to learn
   the icon glyphs. */
.stl-root .priority-tag--labeled {
  /* match the icon-only variant's 5px horizontal gutter so labeled
     and icon-only pills read as the same family — the earlier 8px
     padding left noticeable empty space on either side of the label. */
  padding: 3px 5px;
  gap: 5px;
  cursor: default;
}
.stl-root .priority-tag--labeled .fa {
  font-size: 12px;
  width: 14px;
  text-align: center;
}
.stl-root .priority-tag-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1;
}
/* icon header for the priority column: sits in place of the "Priority" label so
   the column stays narrow. sort caret is still rendered by b-table next to it. */
.stl-root .stl-priority-head {
  font-size: 14px;
  color: #4a5568;
  cursor: help;
}
/* center the icon + sort caret in the priority column header; overrides the
   default left alignment applied to all th/td. */
.stl-root .table.b-table th.stl-priority-th {
  text-align: center !important;
}
.stl-root .priority-tag-info {
  background: #eaf3fb;
  border-color: #4a7ba7;
  color: #1f4a73;
}
.stl-root .priority-tag-warning {
  background: #fff4d1;
  border-color: #b58100;
  color: #7a5400;
}
/* Major Bug / Outage: outlined in the same pastel+border family as the other
   priorities so the column reads as one coherent set; severity still pops via
   the flash animation on the bolt glyph (see .priority-tag--strike below). */
.stl-root .priority-tag-danger {
  background: #fbe3e5;
  border-color: #dc3545;
  color: #dc3545;
}
/* strobe-style flash animation runs only on open urgent rows; the bolt
   briefly spikes brighter and scales up to read as a "lightning strike"
   rather than a constant throb. */
.stl-root .priority-tag--strike .fa-bolt {
  animation: priority-bolt-strike 2s infinite ease-out;
  transform-origin: center center;
}
@keyframes priority-bolt-strike {
  0%, 14%, 100% { transform: scale(1);   filter: brightness(1); }
  4%            { transform: scale(1.25); filter: brightness(1.7); }
  8%            { transform: scale(1);   filter: brightness(1); }
  10%           { transform: scale(1.2); filter: brightness(1.6); }
  13%           { transform: scale(1);   filter: brightness(1); }
}
.stl-root .priority-tag-success {
  background: #edf7ee;
  border-color: #3a8144;
  color: #1f5727;
}
/* outlined orange for Minor Bug — soft orange tint matching the pastel fills
   used by the other outlined priorities (question/enhancement/feature). */
.stl-root .priority-tag-orange-light {
  background: #ffe8d4;
  border-color: #ff6d00;
  color: #ff6d00;
}
/* muted variant — applied when the ticket is completed so the user's eye
   can focus on open/active tickets. opacity also fades the border and icon. */
.stl-root .priority-tag-muted {
  opacity: 0.45;
}

/* VIP signal: tenant name renders in a darker gold with heavier weight.
   chosen over an icon to keep the row visually quiet and non-competing
   with the priority tag in the next column. tooltip carries the reason.
   single-line + ellipsis keeps row heights uniform so column contents
   stay visually aligned across rows. */
.stl-root .tenant-name {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.stl-root .stl-inline-select {
  min-width: 110px;
  max-width: 140px;
  font-size: 13px;
  padding: 0 1.5rem 0 0.35rem;
  height: calc(1.5em + 0.5rem + 2px);
}
.stl-root .stl-inline-select--status {
  min-width: 120px;
  max-width: 160px;
}
.stl-root .stl-inline-select--type {
  min-width: 140px;
  max-width: 180px;
}
/* narrow column — the header word "Billable" is wider than the control,
   so cap it and center the checkbox under the label. */
.stl-root .stl-billable-col {
  width: 70px;
  text-align: center;
}
.stl-root .stl-billable-check {
  display: inline-block;
  margin: 0;
  padding-left: 1.25rem;
}
/* the column header already says "Billable", so the box carries no label
   of its own — keep it readable when disabled (non-admin view / in-flight
   save) instead of bootstrap's near-invisible grey. */
.stl-root .stl-billable-check .custom-control-input:disabled ~ .custom-control-label::before {
  background-color: #fff;
  border-color: #adb5bd;
}
.stl-root .stl-billable-check .custom-control-input:disabled:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}
.stl-root .stl-inline-input {
  font-size: 13px;
  min-width: 160px;
}
.stl-root .stl-inline-input.stl-subject {
  width: 100%;
  max-width: 400px;
}
.stl-root .stl-inline-readonly {
  cursor: pointer;
}
/* dashed-underline edit affordance is for text-style cells only; priority
   pills keep their own solid 1px border. (an earlier border-bottom:none
   override here chopped the pills' bottom edge flat.) */
.stl-root .stl-inline-readonly:not(.priority-tag) {
  border-bottom: 1px dashed transparent;
}
.stl-root .stl-inline-readonly:hover,
.stl-root .stl-inline-readonly:focus {
  outline: none;
}
.stl-root .stl-inline-readonly:not(.priority-tag):hover,
.stl-root .stl-inline-readonly:not(.priority-tag):focus {
  border-bottom-color: #999;
}
.stl-root .stl-person-display {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  vertical-align: middle;
}
.stl-root .stl-team-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #b8d0e4;
  box-shadow: 0 1px 3px rgba(70, 130, 180, 0.12);
  flex: 0 0 auto;
}
.stl-root .priority-tag.stl-inline-readonly:hover,
.stl-root .priority-tag.stl-inline-readonly:focus {
  filter: brightness(0.92);
}
/* row actions (edit / poke / delete). all three are font glyphs so they inherit
   color and follow the theme -- edit and delete used to be edit.png/delete.png,
   black raster art that CSS cannot recolor, so they measured 1.35:1 on
   midnight's --fi-surface and effectively vanished. the poke glyph was themable
   but hardcoded #555 (2.08:1). the muted token measures 6.14:1 on midnight and
   6.13:1 on the light themes, clearing the 3:1 WCAG floor for UI glyphs on
   both. hover is 3.41:1 (midnight) / 3.98:1 (light), delete-hover 6.5:1. */
.stl-root .stl-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: none;
  background: transparent;
  /* !important because the edit action is an <a>, and App.vue's global
     `a:where(:not(.btn)) { color: var(--fi-link) !important }` (App.vue:708)
     otherwise paints it steelblue -- leaving one blue glyph beside two grey
     siblings. :where() keeps that rule at (0,0,1), but important beats
     specificity, so matching it is the only way through. the hover rules below
     need it for the same reason: they would lose to this declaration without
     it. same precedent as the .stl-row-done link colour further down. */
  color: var(--fi-text-muted) !important;
  cursor: pointer;
  text-decoration: none;
}
.stl-root .stl-action-btn:disabled {
  cursor: default;
  opacity: 0.65;
}
.stl-root .stl-action-btn:not(:disabled):hover {
  color: var(--fi-action-primary) !important;
  text-decoration: none;
}
/* destructive action reads red on hover. --fi-chrome-danger rather than
   --fi-danger: the latter has no midnight value, so its #b91c1c would land at
   2.40:1 on the dark surface -- the same failure this block just fixed. */
.stl-root .stl-action-btn--danger:not(:disabled):hover {
  color: var(--fi-chrome-danger) !important;
}
/* admin filter row 2: flex + gap gives consistent spacing; dedicated separator
   spans render as subtle vertical rules that collapse gracefully on wrap. */
.stl-root .stl-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px 16px;
  margin: 6px 0 14px;
  font-size: 14px;
  font-weight: bold;
}
/* bootstrap sizes .btn off its own font-size, and the selects in these two
   rows run at 14px while the buttons default to 16px -- so out of the box the
   Search / Create / All / Cust buttons stand a few px taller than the
   dropdowns beside them. pin both to the same calc bootstrap uses for
   .form-control so they share one baseline height at 14px. the round icon
   affordances (.stl-dev-chip) are deliberately left alone: they're circles,
   and stretching them to control height would distort them. */
.stl-root .stl-filter-row .btn,
.stl-root .stl-filter-row-top .btn {
  font-size: 14px;
  height: calc(1.5em + 0.75rem + 2px);
  display: inline-flex;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

/* ---- the chip filter bar (support_ticket_board.vue's recipe) ---- */
.stl-root .stl-fbar-top { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin: 0 0 8px; text-align: left; }
/* the top strip only carries phone chrome now; on a desktop it is empty
   (or holds the classic-mode buttons) and takes no space when empty */
.stl-root .stl-fbar-top:empty { display: none; }
.stl-root .stl-search-right { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; }
.stl-root .stl-fbar-input--right { width: 220px; font-size: 13px; }
@media (max-width: 768px) {
  .stl-root .stl-desktop-only { display: none !important; }
}
.stl-root .stl-scope-row { display: flex; align-items: center; gap: 10px 14px; flex-wrap: wrap; margin: 0 0 14px; text-align: left; }
.stl-root .stl-tenant-select { max-width: 240px; font-size: 0.9em; padding-top: 3px; padding-bottom: 3px; height: auto; width: auto; }
.stl-root .stl-scope-select { max-width: 220px; font-size: 0.9em; padding-top: 3px; padding-bottom: 3px; height: auto; width: auto; }
.stl-root .stl-group { display: inline-flex; align-items: center; gap: 4px; flex-wrap: wrap; }
/* a hairline between chip sections, with air on both sides, so Status |
   Type | Feature read as separate controls rather than one long run.
   the first section in a row carries none. */
.stl-root .stl-group:not(:first-child) {
  border-left: 1px solid var(--fi-line-strong, #d6dde6);
  padding-left: 18px;
  margin-left: 8px;
}
.stl-root .stl-group-label {
  font-size: 11.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: var(--fi-text-muted, #6b7280);
  margin-right: 4px;
}
.stl-root .stl-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 3px 9px;
  font-size: 11.5px; line-height: 1.5; font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
.stl-root .stl-chip:hover { background: var(--fi-surface-sunken, #eef4fb); }
.stl-root .stl-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.stl-root .stl-chip.stl-chip--active:hover {
  background: var(--fi-ops-accent, #3a6e99); border-color: var(--fi-ops-accent, #3a6e99); color: #fff;
  filter: brightness(1.08);
}
.stl-root .stl-chip--active::before { content: '\f00c'; font-family: FontAwesome; font-size: 8.5px; margin-right: 4px; }
.stl-root .stl-chip.stl-chip--active { box-shadow: 0 0 0 2px var(--fi-surface, #fff), 0 0 0 3.5px currentColor; }
.stl-root .stl-chip-count { margin-left: 3px; font-weight: 700; font-variant-numeric: tabular-nums; opacity: 0.7; }
.stl-root .stl-chip--active .stl-chip-count { opacity: 0.9; }
.stl-root .stl-chip--more { font-weight: 600; opacity: 0.8; }
.stl-root .stl-chip--more .fa { font-size: 10px; margin-right: 3px; }
.stl-root .stl-chip--tone .fa { font-size: 10px; margin-right: 4px; }
.stl-root .stl-chip--tone:hover { filter: brightness(0.96); }
/* tones: the row's tag palette at rest, solid when pressed */
.stl-root .stl-tone-info    { background: #eaf3fb; border-color: #4a7ba7; color: #1f4a73; }
.stl-root .stl-tone-warning { background: #fff4d1; border-color: #b58100; color: #7a5400; }
.stl-root .stl-tone-danger  { background: #fbe3e5; border-color: #dc3545; color: #dc3545; }
.stl-root .stl-tone-success { background: #edf7ee; border-color: #3a8144; color: #1f5727; }
.stl-root .stl-tone-orange  { background: #ffe8d4; border-color: #ff6d00; color: #ff6d00; }
.stl-root .stl-chip--tone.stl-chip--active.stl-tone-info    { background: #4a7ba7; border-color: #4a7ba7; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-tone-warning { background: #b58100; border-color: #b58100; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-tone-danger  { background: #dc3545; border-color: #dc3545; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-tone-success { background: #3a8144; border-color: #3a8144; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-tone-orange  { background: #ff6d00; border-color: #ff6d00; color: #fff; }
.stl-root .stl-flag-vip      { background: #fff4d1; border-color: #b58100; color: #9a6b00; }
.stl-root .stl-flag-billable { background: #e7f8ed; border-color: #3a8144; color: #166534; }
.stl-root .stl-chip--tone.stl-chip--active.stl-flag-vip      { background: #b58100; border-color: #b58100; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-flag-billable { background: #3a8144; border-color: #3a8144; color: #fff; }
.stl-root .stl-feat-general          { background: #eef1f5; border-color: #8a94a1; color: #4b5563; }
.stl-root .stl-feat-actuals          { background: #e7f1ff; border-color: #3b7ddd; color: #0b3d91; }
.stl-root .stl-feat-alerts           { background: #fdeaea; border-color: #d9534f; color: #a12622; }
.stl-root .stl-feat-maps             { background: #e7f8ed; border-color: #3a8144; color: #1f5727; }
.stl-root .stl-feat-reports          { background: #f1ebfb; border-color: #7a4fc4; color: #4c2a8a; }
.stl-root .stl-feat-vsm              { background: #e3f6f4; border-color: #1f9e93; color: #0f5f58; }
.stl-root .stl-feat-data             { background: #eaedfd; border-color: #5560c9; color: #33408f; }
.stl-root .stl-feat-fleet-assistant  { background: #fbe7f4; border-color: #c2418f; color: #8a2b64; }
.stl-root .stl-feat-driver-scorecard { background: #fff4d1; border-color: #b58100; color: #7a5400; }
.stl-root .stl-feat-coaching-portal  { background: #ffe8d4; border-color: #ff6d00; color: #b34c00; }
.stl-root .stl-feat-erm              { background: #f3ede6; border-color: #9a6b3e; color: #6b4423; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-general          { background: #8a94a1; border-color: #8a94a1; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-actuals          { background: #3b7ddd; border-color: #3b7ddd; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-alerts           { background: #d9534f; border-color: #d9534f; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-maps             { background: #3a8144; border-color: #3a8144; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-reports          { background: #7a4fc4; border-color: #7a4fc4; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-vsm              { background: #1f9e93; border-color: #1f9e93; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-data             { background: #5560c9; border-color: #5560c9; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-fleet-assistant  { background: #c2418f; border-color: #c2418f; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-driver-scorecard { background: #b58100; border-color: #b58100; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-coaching-portal  { background: #ff6d00; border-color: #ff6d00; color: #fff; }
.stl-root .stl-chip--tone.stl-chip--active.stl-feat-erm              { background: #9a6b3e; border-color: #9a6b3e; color: #fff; }
/* the photo chips' count bubble, and the selected ring */
.stl-root .stl-dev-chip { position: relative; }
.stl-root .stl-dev-chip--selected { box-shadow: 0 0 0 1px var(--fi-surface, #fff), 0 0 0 3px var(--fi-ops-accent, #3a6e99); }
.stl-root .stl-dev-chip-count {
  position: absolute; right: -5px; bottom: -4px;
  min-width: 15px; height: 15px; padding: 0 4px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  border: 1.5px solid var(--fi-surface, #fff);
  font-size: 9.5px; font-weight: 700; line-height: 12px;
  font-variant-numeric: tabular-nums; text-align: center;
  pointer-events: none;
}
/* the "Filtering by" strip */
.stl-root .stl-active {
  display: flex; align-items: center; flex-wrap: wrap; gap: 5px 6px;
  margin: 0 0 10px; padding: 5px 9px;
  border: 1px solid rgba(58, 110, 153, 0.35); border-left: 3px solid var(--fi-ops-accent, #3a6e99);
  border-radius: 6px;
  background: rgba(58, 110, 153, 0.07);
  font-size: 11.5px; text-align: left;
}
.stl-root .stl-active-label { font-weight: 700; color: var(--fi-ops-accent, #3a6e99); margin-right: 2px; }
.stl-root .stl-active-label .fa { font-size: 10px; margin-right: 2px; }
.stl-root .stl-active-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 7px 1px 8px;
  border: 1px solid var(--fi-ops-accent, #3a6e99); border-radius: 999px;
  background: var(--fi-surface, #fff); color: var(--fi-text, #374151);
  font-size: 11px; font-weight: 600; cursor: pointer;
}
.stl-root .stl-active-pill:hover { background: rgba(58, 110, 153, 0.12); }
.stl-root .stl-active-pill .fa { font-size: 9px; color: var(--fi-text-muted, #8a94a1); }
.stl-root .stl-active-pill:hover .fa { color: #b91c1c; }
.stl-root .stl-active-dim { font-weight: 400; color: var(--fi-text-muted, #6b7280); }
.stl-root .stl-link-btn {
  background: none; border: 0; padding: 0; font-size: inherit;
  color: var(--fi-ops-accent, #3a6e99); text-decoration: underline; cursor: pointer;
}
.stl-root .stl-active-clear { margin-left: auto; font-size: 11px; }

/* ---- the chip filter bar under midnight ----
   the tone families above are pastel-fill / dark-ink pills drawn for a white
   page. left alone on the dark canvas they are the brightest thing on the
   screen, so an UNPRESSED chip out-shouts a pressed one and the whole bar
   reads as fully selected. same eleven feature hues and five tone hues here,
   taken as a low-alpha wash of the hue with its light tint as ink, so a chip
   still reads as its family while sitting at surface level; the pressed state
   keeps its solid fill, which is what makes the pressed/unpressed split
   legible again.

   scoped on the attribute alone (the pattern support_ticket_board.vue already
   uses): the list only ever renders inside the horizon shell, and midnight is
   horizon's dark variant, so there is no standalone-page case to protect. */
/* both :not()s are load-bearing. Against .stl-chip--active this selector
   would strip the pressed chip's #fff ink and accent border; against the
   .stl-tone-* / .stl-feat-* / .stl-flag-* rules below it ties on specificity
   and would win or lose on source order alone, so the exclusion is stated
   rather than left to the ordering. This governs the plain chips only. */
[data-fi-theme="midnight"] .stl-root .stl-chip:not(.stl-chip--active):not(.stl-chip--tone) {
  /* --fi-line is a 0.09 white hairline on dark — too faint to read as a
     control when a whole row of them sits together */
  border-color: var(--fi-line-strong, rgba(255, 255, 255, 0.16));
  color: var(--fi-text, #d7e1ea);
}
/* --fi-surface-sunken is DARKER than --fi-surface under midnight, so the
   light-mode hovers below would sink a chip instead of lifting it */
[data-fi-theme="midnight"] .stl-root .stl-chip:hover { background: rgba(255, 255, 255, 0.07); }
[data-fi-theme="midnight"] .stl-root .stl-chip--tone:hover { filter: brightness(1.22); }
[data-fi-theme="midnight"] .stl-root .stl-filter-toggle:hover { background: rgba(255, 255, 255, 0.07); }
/* the pressed ring is white-on-white in light — invisible by construction —
   but on dark the same currentColor turns into a glaring halo */
[data-fi-theme="midnight"] .stl-root .stl-chip.stl-chip--active {
  box-shadow: 0 0 0 2px var(--fi-surface, #182534), 0 0 0 3.5px rgba(255, 255, 255, 0.26);
}

[data-fi-theme="midnight"] .stl-root .stl-tone-info    { background: rgba(74, 123, 167, 0.18);  border-color: rgba(74, 123, 167, 0.5);  color: #9cc4ea; }
[data-fi-theme="midnight"] .stl-root .stl-tone-warning { background: rgba(181, 129, 0, 0.20);   border-color: rgba(181, 129, 0, 0.5);   color: #e5bd5c; }
[data-fi-theme="midnight"] .stl-root .stl-tone-danger  { background: rgba(220, 53, 69, 0.18);   border-color: rgba(220, 53, 69, 0.5);   color: #f2949c; }
[data-fi-theme="midnight"] .stl-root .stl-tone-success { background: rgba(58, 129, 68, 0.20);   border-color: rgba(58, 129, 68, 0.5);   color: #85cf94; }
[data-fi-theme="midnight"] .stl-root .stl-tone-orange  { background: rgba(255, 109, 0, 0.18);   border-color: rgba(255, 109, 0, 0.5);   color: #ffab63; }
[data-fi-theme="midnight"] .stl-root .stl-flag-vip      { background: rgba(181, 129, 0, 0.20);  border-color: rgba(181, 129, 0, 0.5);   color: #e5bd5c; }
[data-fi-theme="midnight"] .stl-root .stl-flag-billable { background: rgba(58, 129, 68, 0.20);  border-color: rgba(58, 129, 68, 0.5);   color: #85cf94; }
[data-fi-theme="midnight"] .stl-root .stl-feat-general          { background: rgba(138, 148, 161, 0.20); border-color: rgba(138, 148, 161, 0.5); color: #bcc5d0; }
[data-fi-theme="midnight"] .stl-root .stl-feat-actuals          { background: rgba(59, 125, 221, 0.20);  border-color: rgba(59, 125, 221, 0.5);  color: #8fb8f0; }
[data-fi-theme="midnight"] .stl-root .stl-feat-alerts           { background: rgba(217, 83, 79, 0.20);   border-color: rgba(217, 83, 79, 0.5);   color: #f0918e; }
[data-fi-theme="midnight"] .stl-root .stl-feat-maps             { background: rgba(58, 129, 68, 0.20);   border-color: rgba(58, 129, 68, 0.5);   color: #85cf94; }
[data-fi-theme="midnight"] .stl-root .stl-feat-reports          { background: rgba(122, 79, 196, 0.22);  border-color: rgba(122, 79, 196, 0.5);  color: #bda4e9; }
[data-fi-theme="midnight"] .stl-root .stl-feat-vsm              { background: rgba(31, 158, 147, 0.20);  border-color: rgba(31, 158, 147, 0.5);  color: #6ed3c9; }
[data-fi-theme="midnight"] .stl-root .stl-feat-data             { background: rgba(85, 96, 201, 0.22);   border-color: rgba(85, 96, 201, 0.5);   color: #a4abe9; }
[data-fi-theme="midnight"] .stl-root .stl-feat-fleet-assistant  { background: rgba(194, 65, 143, 0.20);  border-color: rgba(194, 65, 143, 0.5);  color: #ea9cca; }
[data-fi-theme="midnight"] .stl-root .stl-feat-driver-scorecard { background: rgba(181, 129, 0, 0.20);   border-color: rgba(181, 129, 0, 0.5);   color: #e5bd5c; }
[data-fi-theme="midnight"] .stl-root .stl-feat-coaching-portal  { background: rgba(255, 109, 0, 0.18);   border-color: rgba(255, 109, 0, 0.5);   color: #ffab63; }
[data-fi-theme="midnight"] .stl-root .stl-feat-erm              { background: rgba(154, 107, 62, 0.22);  border-color: rgba(154, 107, 62, 0.5);  color: #d5ab7d; }

/* --fi-ops-accent (#3a6e99) is a mid-dark blue picked to carry on white; on
   the dark canvas it drops under AA, so the accent-colored chrome — the
   selected photo ring, the "Filtering by" strip and its pills — moves to the
   shell's lifted link blue. the strip's 0.07 fill vanishes outright on dark. */
[data-fi-theme="midnight"] .stl-root .stl-dev-chip--selected {
  box-shadow: 0 0 0 1px var(--fi-surface, #182534), 0 0 0 3px var(--fi-link, #6fb1e8);
}
[data-fi-theme="midnight"] .stl-root .stl-active {
  border-color: rgba(111, 177, 232, 0.32);
  border-left-color: var(--fi-link, #6fb1e8);
  background: rgba(111, 177, 232, 0.10);
}
[data-fi-theme="midnight"] .stl-root .stl-active-label { color: var(--fi-link, #6fb1e8); }
[data-fi-theme="midnight"] .stl-root .stl-active-pill { border-color: rgba(111, 177, 232, 0.5); }
[data-fi-theme="midnight"] .stl-root .stl-active-pill:hover { background: rgba(111, 177, 232, 0.16); }
/* #b91c1c is a near-black red on this canvas — the remove affordance has to
   read as red without going invisible */
[data-fi-theme="midnight"] .stl-root .stl-active-pill:hover .fa { color: #f08a8a; }
[data-fi-theme="midnight"] .stl-root .stl-link-btn { color: var(--fi-link, #6fb1e8); }

.stl-root .stl-fbar-count {
  white-space: nowrap;
}
.stl-root .stl-fbar-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
/* the whole row opens the ticket; cells with their own click keep their
   own cursor (the inline-edit and action rules set theirs) */
.stl-root .table.b-table tbody tr.stl-row-clickable { cursor: pointer; }

.stl-root .stl-fbar-select {
  font-size: 14px;
  font-weight: bold;
}
/* the search slot rides the filter row inline with the selects. bootstrap's
   .form-control is display:block, which would drop the input onto its own
   line inside the span, hence the inline-block. sized to hold a subject
   fragment without crowding the dropdowns beside it. */
.stl-root .stl-search-slot {
  white-space: nowrap;
}
.stl-root .stl-fbar-input {
  font-size: 14px;
  font-weight: bold;
  width: 200px;
  max-width: 100%;
  display: inline-block;
  vertical-align: middle;
}
.stl-root .stl-btn-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.stl-root .stl-dev-team-chips {
  gap: 6px;
}
.stl-root .stl-dev-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.stl-root .stl-dev-chip:hover {
  border-color: #6c757d;
  transform: translateY(-1px);
}
.stl-root .stl-dev-chip--selected {
  border-color: var(--fi-action-primary);
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.35);
}
.stl-root .stl-dev-chip-img {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.stl-root .stl-dev-chip-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #eef0f2;
  color: #666;
  font-size: 14px;
  line-height: 1;
}
.stl-root .stl-dev-chip-icon--unassigned::after {
  content: '';
  position: absolute;
  left: 3px;
  right: 3px;
  top: 50%;
  height: 2px;
  margin-top: -1px;
  background: #c0392b;
  transform: rotate(-45deg);
  border-radius: 1px;
  pointer-events: none;
}
.stl-root .stl-fbar-sep {
  display: inline-block;
  width: 1px;
  height: 18px;
  background: #ccc;
}
.stl-root .stl-fbar-vip {
  font-size: 14px;
  font-weight: bold;
  margin: 0;
  white-space: nowrap;
}

/* below the navbar's mobile breakpoint, the quick-filter button groups and
   their separators wrap into a tall stack that dwarfs the real filters. hide
   them so the remaining dropdowns + search + VIP toggle stay usable. */
@media (max-width: 767px) {
  .stl-root .stl-btn-group,
  .stl-root .stl-fbar-sep {
    display: none;
  }
}

/* VIP signal lives on the row background now; tenant name stays uniform with
   non-VIP rows. cursor stays as a hover affordance for the tooltip. */
.stl-root .tenant-name.is-vip {
  cursor: help;
}
/* fade when the ticket is completed/cancelled so the eye stays on open work */
.stl-root .tenant-name.is-muted {
  opacity: 0.55;
}

/* VIP signal: thin gold bar on the row's leftmost edge. a pseudo-element lets
   us switch between solid (open) and hollow (closed) fills without shifting
   cell content or disrupting BV's default table borders. */
.stl-root .stl-row-vip td:first-child {
  position: relative;
}
.stl-root .stl-row-vip td:first-child::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #d4a017;
  box-sizing: border-box;
}
/* closed VIP tickets get a hollow version: gold outline, white interior. */
.stl-root .stl-row-vip.stl-row-done td:first-child::before {
  background: #fff;
  border: 1px solid #d4a017;
}
/* completed tickets fade to gray across the whole row so active work stands
   out. !important on the link color beats an app-level anchor rule inherited
   from the help-center wrapper. team avatars are raster so they fade via
   opacity rather than color. */
.stl-root .stl-row-done td,
.stl-root .stl-row-done td a,
.stl-root .stl-row-done td a:hover {
  color: #9098a1 !important;
}
.stl-root .stl-row-done td img {
  opacity: 0.4;
}
/* the row actions fade too, but hold their own muted color and recede by
   opacity instead -- a done ticket is still editable and deletable, so the
   glyphs have to stay legible. 0.65 matches the disabled state and keeps them
   at 3.43:1 on midnight, above the 3:1 floor for interactive glyphs; inheriting
   the row's #9098a1 at 0.4 would have dropped them to 2.13:1. */
.stl-root .stl-row-done td a.stl-action-btn {
  color: var(--fi-text-muted) !important;
}
.stl-root .stl-row-done .stl-action-btn {
  opacity: 0.65;
}

/* subject wraps to show full text; column width caps line length. */
.stl-root .stl-subject {
  display: inline-block;
  max-width: 400px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  vertical-align: middle;
  line-height: 1.35;
}

/* status cell — single-line, prevents the two-line wrap that was happening for
   "Under Review" / "Customer Testing" and was inflating row height. full label
   stays in the title tooltip (see statusShortLabel map). */
.stl-root .stl-status {
  display: inline-block;
  white-space: nowrap;
  cursor: help;
}

/* compact datetime cell — single-line; full ISO available on hover via :title */
.stl-root .stl-dt {
  display: inline-block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.stl-root .stl-dt-overdue {
  color: darkred;
}

.error td {
  background-color: rgba(255, 0, 0, 0.25)
}

.success td {
  background-color: hsla(120, 100%, 75%, 0.3)
}

.unprocessed td {
  background-color: hsla(200, 40%, 80%, 0.3)
}

.cancelled td {
  background-color: hsla(290, 60%, 70%, 0.3)
}

.header {
  font-weight: bold;
}

.list-text {
  text-align: left;
  margin-top: 7px;
  margin-left: 7px;
}

.list-option {
  text-align: left;
  margin: 2px;
}
</style>
